/* eslint-disable @typescript-eslint/no-explicit-any -- Legacy MasterSync still bridges heterogeneous offline payloads across attendance, operations, and connect domains. */
/**
 * MasterSync - Unified Offline Synchronization Service
 * 
 * Coordinates sync across all modules when coming back online:
 * - total-attendance: Face templates, employees, shifts, pending attendance logs
 * - total-operations: Work items (jobs, tasks, signatures, photos)
 * - total-connect: Messages, email drafts/outbox
 * 
 * Features:
 * - Module-aware: Only syncs data for modules user has access to
 * - Priority queue: Syncs in order of importance (attendance first)
 * - Progress tracking: Granular progress for UI display
 * - Retry logic: Exponential backoff for failed syncs
 * - Conflict resolution: Configurable per-module (server_wins, keep_local, keep_server, merge, manual)
 * 
 * Uses unified offline stores for attendance, operations, and connect data,
 * with unified offline stores handling queue persistence and cached snapshots.
 * 
 * @author TotalAccess Development Team
 * @date January 2026
 */

import type { OfflineAttendanceData, PendingAttendanceLog } from './offline/attendanceSnapshotTypes';
import type { OfflineWorkItemSnapshot as StoredOfflineWorkItem } from './offline/workItemSnapshotTypes';
import type {
  AttendanceCompanyStatsSummary,
  AttendancePersonalStatsSummary,
} from '@/components/attendance/utils/attendanceDashboardShared';
import { offlineEmail } from './offlineEmail';
import { debugLog } from './debugLogger';
import { offlineMessages } from './offlineMessages';
import offlineDb, { type OfflineDomain, type PublishRetryRecord } from './offline/offlineDb';
import {
  loadOperationsWorkItemsList,
  saveOperationsWorkItemsList,
  saveQueuedEquipmentRecord,
} from './offline/operationsWorkItemsListCache';
import appShellStore from './offline/appShellStore';
import attendanceStore from './offline/attendanceStore';
import { setCachedFaceMatchThreshold } from './offline/faceThresholdCache';
import workItemsStore from './offline/workItemsStore';
import workflowGroupMembershipStore from './offline/workflowGroupMembershipStore';
import effectivePermissionsStore from './offline/effectivePermissionsStore';
import offlineStorage, {
  type OfflineClockAction,
  type OfflineEmployee,
  type OfflineFaceTemplate,
  type OfflineGeofence,
  type OfflineShiftTemplate,
  type OfflineSyncMetadata,
} from './offlineStorage';
import offlineWorkItems, {
  type OfflineWorkItemPhoto,
  type OfflineWorkItemSignature,
} from './offlineWorkItems';
import { networkState } from './networkState';
import { isCapacitor, buildApiUrl, isTauri } from './platformUtils';
import { isSilentFetchError } from '@/lib/capacitorFetchPatch';
import { apiService } from './apiService';
import { authService } from './authService';
import { getOfflineMutationLedgerSummary } from './offlineMutationLedger';
import {
  syncPendingChildEntries,
  getPendingChildEntryCount,
  loadPendingChildEntries,
  savePendingChildEntries,
  type PendingChildEntry,
  type PendingChildEntryPayload,
} from '@/lib/operations/smartTaskOfflineEntries';
import {
  syncPendingQuoteRequests,
  getPendingQuoteRequestCount,
  loadPendingQuoteRequests,
  savePendingQuoteRequests,
  type PendingQuoteRequest,
  type PendingQuoteRequestPayload,
} from '@/lib/operations/smartTaskOfflineQuoteRequests';
import {
  syncPendingBomItems,
  getPendingBomItemCount,
  loadPendingBomItems,
  savePendingBomItems,
  type PendingBomItem,
  type PendingBomItemPayload,
} from '@/lib/operations/smartTaskOfflineBom';
import {
  syncPendingInventoryMovements,
  syncPendingStockCountActions,
  getPendingInventoryMovementCount,
  getPendingStockCountActionCount,
} from '@/lib/operations/smartTaskOfflineInventory';
import {
  loadPendingSmartTaskComments,
  syncPendingSmartTaskActions,
  loadCachedSmartTaskWorkflowState,
  loadSmartTaskOfflineActionHistory,
  loadPendingSmartTaskActions,
  queueSmartTaskOfflineCommentCreate,
  queueSmartTaskOfflineFleetAction,
  queueSmartTaskOfflineFieldServiceAction,
  queueSmartTaskOfflineLogbookAction,
  queueSmartTaskOfflineStatusUpdate,
  queueSmartTaskOfflineRouteStopUpdate,
  queueSmartTaskOfflineVehicleAction,
  queueSmartTaskOfflineWorkflowReset,
  queueSmartTaskOfflineTransition,
  saveCachedSmartTaskWorkflowState,
  type SmartTaskOfflineActivityContext,
  type SmartTaskOfflineActionHistoryEntry,
  type SmartTaskFleetAction,
  type SmartTaskFieldServiceAction,
  type SmartTaskLogbookAction,
  type SmartTaskQueuedAction,
  type SmartTaskQueuedCommentCreate,
  type SmartTaskVehicleAction,
} from '@/lib/operations/smartTaskOfflineActions';
import { emitOperationsSyncPendingChanged } from '@/lib/operations/offlineSyncEvents';
import {
  cacheOfflineFormConfig,
  clearOfflineFormDraft,
  getCachedOfflineFormConfig,
  getOfflineFormDraft,
  getOfflineFormQueuePendingCount,
  predownloadOperationalFormConfigs,
  queueOfflineFormSubmission,
  saveOfflineFormDraft,
  syncOfflineFormQueueNow,
  type OperationalFormWorkItemTarget,
  type QueuedSubmission,
  type StoredFormDraft,
} from '@/hooks/useOfflineFormQueue';
import type { WorkflowStateRecord } from '@/app/client-zone/modules/total-operations/components/smart-task/types';
import {
  buildSmartTaskAssignedSourceParams,
  buildSmartTaskRecentClosedSourceParams,
  SMART_TASK_SOURCE_CACHE_KEY,
} from '@/app/client-zone/modules/total-operations/components/smart-task/utils/workItemFilters';
import type { WorkItemCommentType } from '@/lib/work-items/commentTypes';
import {
  setModuleStrategy,
  getModuleStrategy,
  detectConflict,
  resolveConflict,
  createConflictEntry,
  mergeData,
  processConflicts,
  type ConflictStrategy,
  type ConflictEntry,
  type SyncModuleName,
} from './offlineConflictResolver';

// ==================== TYPES ====================

export type SyncModule = 'attendance' | 'operations' | 'connect';

export type SyncStage =
  | 'idle'
  | 'checking'
  | 'uploading-attendance'
  | 'uploading-messages'
  | 'uploading-emails'
  | 'uploading-workitems'
  | 'downloading-attendance'
  | 'downloading-messages'
  | 'cleanup-pruning'
  | 'complete'
  | 'error';

export interface ModuleAccess {
  attendance: boolean;
  operations: boolean;
  connect: boolean;
}

export interface SyncProgress {
  stage: SyncStage;
  module: SyncModule | null;
  message: string;
  current: number;
  total: number;
  error?: string;
}

export interface SyncResult {
  success: boolean;
  module: SyncModule;
  uploaded: number;
  downloaded: number;
  failed: number;
  errors: string[];
  durationMs: number;
  conflictsDetected?: number;
  conflictsResolved?: number;
  conflictsManual?: number;
}

export interface OperationSyncStepResult {
  key: 'work-items' | 'uploads' | 'child-entries' | 'quote-requests' | 'bom-items' | 'forms' | 'workflow-actions' | 'inventory-movements' | 'stock-counts';
  label: string;
  success: boolean;
  uploaded: number;
  failed: number;
  errors: string[];
  durationMs: number;
}

export interface OperationalFormTemplateSyncResult {
  success: boolean;
  cached: number;
  failed: number;
  cachedFormKeys: string[];
}

export type AttendanceConfigSyncType = 'geofences' | 'shifts' | 'employees' | 'templates';

export interface AttendanceConfigSyncResult {
  success: boolean;
  configType: AttendanceConfigSyncType;
  mode: 'personal' | 'kiosk' | 'single';
  downloadedEmployees: number;
  downloadedTemplates: number;
  downloadedShifts: number;
  downloadedGeofences: number;
}

export interface PrepareAttendanceOfflineAssetsOptions {
  pageStepIndex: number;
  modelStepIndex: number;
  updateProgress: (
    step: string,
    index: number,
    status: 'loading' | 'success' | 'error',
    count?: number,
  ) => void;
  logPrefix?: string;
}

export interface PrepareAttendanceOfflineAssetsResult {
  pageCacheResult: {
    success: boolean;
    cached?: number;
    total?: number;
    error?: string;
  };
  modelCacheResult: {
    success: boolean;
    ready: boolean;
    mediaPipe: { cached: number; failed: number; total: number };
    faceApi: { cached: number; failed: number; total: number };
    error?: string;
  };
  libraryLoaded: boolean;
  modelsFullyLoaded: boolean;
  modelsReady: boolean;
  totalModelsCached: number;
  error?: string;
}

export interface CachedAttendanceGeofence {
  id: string | number;
  name: string;
  latitude: number;
  longitude: number;
  radius_meters: number;
  identifiers: any[];
}

export type AttendancePersonalStatsCache = AttendancePersonalStatsSummary;

export type AttendanceCompanyStatsCache = AttendanceCompanyStatsSummary;

export interface CachedOperationsWorkItem {
  id: string | number;
  deleted_at?: string | null;
  [key: string]: any;
}

export interface QueuedFleetInspection {
  id: string;
  data: Record<string, any>;
  location_lat?: number;
  location_lng?: number;
  createdAt: string;
  retryCount: number;
}

export interface VehicleInspectionDraft {
  vehicleId: string;
  capturedPhotos: Record<string, string>;
  checklistItems: Record<string, 'pass' | 'fail' | null>;
  fuelLevel: string;
  odometerReading: string;
  notes: string;
  savedAt: number;
}

export interface LastVehicleInspectionSnapshot {
  vehicleId: string;
  inspectionId: string | null;
  inspectionDate: string | null;
  checklistItems: { item?: string; status?: string; notes?: string | null }[];
  odometerReading?: number | null;
  savedAt: number;
}

export interface PersistedSmartTaskRoutePlan {
  travelSessionId: string;
  routePlanId: string;
  workItemId?: string | null;
  savedAt: string;
}

export interface SmartTaskEquipmentOfflineRecord {
  item_id: string | number;
  action: string;
  payload: Record<string, any>;
  photos?: Record<string, string>;
}

export interface GenericFormDraftRecord {
  id: string;
  formKey: string;
  formConfigId: number;
  entityType?: string;
  entityId?: string | number;
  formData: Record<string, any>;
}

export interface MasterSyncStats {
  pendingAttendanceLogs: number;
  pendingMessages: number;
  pendingEmails: number;
  pendingWorkItems: number;
  pendingWorkItemUploads: number;
  pendingChildEntries: number;
  pendingFormSubmissions: number;
  pendingSmartTaskActions: number;
  pendingInventoryMovements: number;
  pendingStockCountActions: number;
  pendingMutationLedger: number;
  failedMutationLedger: number;
  conflictMutationLedger: number;
  pendingAttendanceMutations: number;
  pendingOperationsMutations: number;
  pendingConnectMutations: number;
  oldestPendingMutationAgeMs: number | null;
  lastFullSync: Date | null;
  lastSyncResults: SyncResult[];
  moduleAccess: ModuleAccess;
}

export interface MasterSyncOperationsBreakdown {
  pendingWorkItems: number;
  pendingWorkItemUploads: number;
  pendingChildEntries: number;
  pendingFormSubmissions: number;
  pendingSmartTaskActions: number;
  pendingInventoryMovements: number;
  pendingStockCountActions: number;
}

export interface MasterSyncPrepareOfflineOptions {
  attendanceMode?: 'personal' | 'kiosk' | 'single' | 'auto';
  workItems?: Array<OperationalFormWorkItemTarget & Record<string, any>>;
}

export interface MasterSyncPrepareOfflineResult {
  success: boolean;
  attendance: {
    downloadedEmployees: number;
    downloadedTemplates: number;
    downloadedShifts: number;
    downloadedGeofences: number;
    mode: 'personal' | 'kiosk' | 'single';
  } | null;
  operations: {
    cachedWorkItems: number;
    cachedForms: number;
    cachedVehicles: number;
    failedForms: number;
  } | null;
  errors: string[];
}

export interface MasterSyncConfig {
  clientId: number;
  userId: number;
  employeeId?: number;
  authToken: string;
  moduleAccess: ModuleAccess;
  onProgress?: (progress: SyncProgress) => void;
  onComplete?: (results: SyncResult[]) => void;
  onError?: (error: Error) => void;
}

// ==================== CONSTANTS ====================

const SYNC_PRIORITIES: SyncModule[] = ['attendance', 'operations', 'connect'];

const WORK_ITEM_CACHE_KEY = 'work_item_cache';
const WORK_ITEM_FILTER_CACHE_PREFIX = 'work_item_cache_filter_';
const FLEET_VEHICLES_CACHE_KEY = 'fleet_vehicles_cache';
const LEGACY_FLEET_INSPECTION_QUEUE_KEY = 'smart_task_v2_offline_inspections';
const VEHICLE_INSPECTION_DRAFT_PREFIX = 'inspection_draft_';
const LAST_VEHICLE_INSPECTION_SNAPSHOT_PREFIX = 'last_vehicle_inspection_';
const SMARTTASK_ROUTE_PLAN_PREFIX = 'smarttask.activeRoutePlan.';
const VEHICLE_INSPECTION_DRAFT_ENTITY_PREFIX = 'smarttask.vehicleInspectionDraft.';
const LAST_VEHICLE_INSPECTION_SNAPSHOT_ENTITY_PREFIX = 'smarttask.lastVehicleInspection.';
const SMARTTASK_ROUTE_PLAN_ENTITY_PREFIX = 'smarttask.routePlan.';
const MASTERSYNC_METADATA_STORAGE_PREFIX = 'ta.masterSync.metadata.v1';
const LAST_CLEANUP_AT_STORAGE_KEY = 'ta.masterSync.lastCleanupAt.v1';
const CLEANUP_MAX_AGE_DAYS = 7;
const CLEANUP_INTERVAL_MS = 24 * 60 * 60 * 1000; // 24 hours

// Cold-start cache validation — detects stale cache data that can cause the
// app to hang on loading. If the last successful sync is older than this
// threshold, or if cached navigation data is stale, the cache is proactively
// cleaned to force a fresh fetch rather than looping on stale credentials.
const COLD_START_CACHE_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000; // 7 days
const COLD_START_CACHE_CHECK_KEY = 'ta.masterSync.coldStartCacheCheck.v1';
const STALE_NAVIGATION_DATA_KEY = 'offline_navigation_data';

type AttendanceQueueStatus = 'clocked_out' | 'clocked_in' | 'on_break';

type QueueSmartTaskPhotoInput = Omit<OfflineWorkItemPhoto, 'id' | 'thumbnail_base64' | 'uploaded' | 'synced' | 'upload_attempts' | 'created_at'>;
type QueueSmartTaskSignatureInput = Omit<OfflineWorkItemSignature, 'id' | 'uploaded' | 'synced' | 'upload_attempts' | 'created_at'>;

function getAttendanceStatusFromAction(action: PendingAttendanceLog['action'] | PendingAttendanceLog['action_type']): AttendanceQueueStatus {
  switch (action) {
    case 'clock_in':
    case 'break_out':
      return 'clocked_in';
    case 'clock_out':
      return 'clocked_out';
    case 'break_in':
      return 'on_break';
    default:
      return 'clocked_out';
  }
}

function generateMasterSyncQueueId(prefix: string): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `${prefix}_${crypto.randomUUID()}`;
  }

  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

const ATTENDANCE_UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-/i;

function toFiniteNumber(value: unknown, fallback = 0): number {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : fallback;
}

function toOptionalFiniteNumber(value: unknown): number | undefined {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : undefined;
}

function toAttendanceDate(value?: string | Date | null): Date {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value;
  }

  if (typeof value === 'string') {
    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed;
    }
  }

  return new Date();
}

function toAttendanceIsoString(value?: string | Date | null): string {
  return toAttendanceDate(value).toISOString();
}

function normalizeQueuedAttendanceAction(action: string | undefined): PendingAttendanceLog['action'] {
  switch (action) {
    case 'clock_in':
    case 'clock_out':
    case 'break_in':
    case 'break_out':
      return action;
    case 'start_break':
      return 'break_in';
    case 'end_break':
      return 'break_out';
    default:
      return 'clock_out';
  }
}

function generateAttendanceOfflineId(existingId?: string): string {
  if (existingId && ATTENDANCE_UUID_PATTERN.test(existingId)) {
    return existingId;
  }

  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return `attendance_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

function buildStablePendingLogNumericId(logId: string): number {
  let hash = 0;
  for (let index = 0; index < logId.length; index += 1) {
    hash = ((hash << 5) - hash + logId.charCodeAt(index)) | 0;
  }

  return Math.abs(hash) + 1_000_000_000;
}

function mapUnifiedAttendanceEmployee(
  employee: OfflineEmployee,
  dedicatedStatusMap?: Map<string, string>,
) {
  const employeeId = toFiniteNumber(employee.employee_id ?? employee.id);
  const dedicatedStatus = dedicatedStatusMap?.get(String(employeeId));

  return {
    ...employee,
    id: toFiniteNumber(employee.id ?? employee.employee_id),
    employee_id: employeeId,
    user_id: employee.user_id != null ? Number(employee.user_id) : null,
    employee_number: String(employee.employee_number ?? ''),
    first_name: String(employee.first_name ?? ''),
    last_name: String(employee.last_name ?? ''),
    location_flexible: Boolean(employee.location_flexible),
    shift_template_id: toOptionalFiniteNumber(employee.shift_template_id),
    current_status: (dedicatedStatus || employee.current_status || 'clocked_out') as OfflineAttendanceData['employees'][number]['current_status'],
  };
}

function mapUnifiedAttendanceFaceTemplate(template: OfflineFaceTemplate) {
  return {
    ...template,
    id: toFiniteNumber(template.id),
    employee_id: toFiniteNumber(template.employee_id),
    descriptor: template.descriptor ? Array.from(template.descriptor) : [],
    quality_score: toFiniteNumber(template.quality_score),
    is_active: template.is_active !== false,
  };
}

function mapUnifiedAttendanceShiftTemplate(shift: OfflineShiftTemplate) {
  return {
    ...shift,
    id: toFiniteNumber(shift.id),
    break_duration_minutes: toFiniteNumber(shift.break_duration_minutes),
    location_ids: Array.isArray(shift.location_ids)
      ? shift.location_ids.map((locationId) => toFiniteNumber(locationId)).filter((locationId) => Number.isFinite(locationId))
      : [],
    requires_geofence: Boolean(shift.requires_geofence),
  };
}

function mapUnifiedAttendanceGeofenceIdentifiers(
  identifiers: OfflineGeofence['identifiers'] | undefined,
): OfflineAttendanceData['geofences'][number]['identifiers'] | undefined {
  if (!identifiers) {
    return undefined;
  }

  const gps = Array.isArray(identifiers.gps)
    ? identifiers.gps
      .map((entry) => ({
        latitude: toFiniteNumber(entry.latitude),
        longitude: toFiniteNumber(entry.longitude),
        radius: toFiniteNumber(entry.radius, 100),
      }))
      .filter((entry) => entry.latitude !== 0 || entry.longitude !== 0)
    : [];

  const wifi = Array.isArray(identifiers.wifi)
    ? identifiers.wifi
      .map((entry) => {
        const wifiRecord = entry as {
          ssid?: unknown;
          bssid?: unknown;
          bssids?: unknown;
        };

        const bssids = Array.isArray(wifiRecord.bssids) ? wifiRecord.bssids : [];
        const firstBssid = typeof bssids[0] === 'string' ? bssids[0] : undefined;

        return {
          ssid: typeof wifiRecord.ssid === 'string' ? wifiRecord.ssid : '',
          bssid: typeof wifiRecord.bssid === 'string' ? wifiRecord.bssid : firstBssid,
        };
      })
      .filter((entry) => entry.ssid.length > 0)
    : [];

  const ip = Array.isArray(identifiers.ip_ranges)
    ? identifiers.ip_ranges
      .map((entry) => {
        const ipRecord = entry as {
          address?: unknown;
          cidr?: unknown;
          range?: unknown;
          start?: unknown;
          end?: unknown;
        };

        const address = typeof ipRecord.address === 'string'
          ? ipRecord.address
          : typeof ipRecord.cidr === 'string'
            ? ipRecord.cidr
            : typeof ipRecord.start === 'string'
              ? ipRecord.start
              : '';

        const range = typeof ipRecord.range === 'string'
          ? ipRecord.range
          : typeof ipRecord.end === 'string'
            ? ipRecord.end
            : undefined;

        return { address, range };
      })
      .filter((entry) => entry.address.length > 0)
    : [];

  if (gps.length === 0 && wifi.length === 0 && ip.length === 0) {
    return undefined;
  }

  return {
    gps,
    wifi,
    ip,
  };
}

function mapUnifiedAttendanceGeofence(geofence: OfflineGeofence) {
  return {
    ...geofence,
    id: toFiniteNumber(geofence.id),
    center_latitude: toFiniteNumber(geofence.center_latitude),
    center_longitude: toFiniteNumber(geofence.center_longitude),
    radius: toFiniteNumber(geofence.radius, 100),
    identifiers: mapUnifiedAttendanceGeofenceIdentifiers(geofence.identifiers),
  };
}

function mapUnifiedPendingClockAction(action: OfflineClockAction, preferredId?: string): PendingAttendanceLog {
  const normalizedAction = normalizeQueuedAttendanceAction(action.action_type || action.action);

  return {
    id: preferredId || action.offline_id || String(action.id || generateAttendanceOfflineId()),
    employee_id: toFiniteNumber(action.employee_id),
    action: normalizedAction,
    action_type: normalizedAction,
    timestamp: toAttendanceIsoString(action.timestamp),
    latitude: action.latitude ?? action.gps_latitude,
    longitude: action.longitude ?? action.gps_longitude,
    accuracy: action.accuracy ?? action.gps_accuracy,
    wifi_ssid: action.wifi_ssid,
    wifi_bssid: action.wifi_bssid,
    signal_strength: action.signal_strength,
    beacon_uuid: action.beacon_uuid,
    template_id: action.template_id,
    confidence: action.confidence ?? action.confidence_score,
    face_descriptor: action.face_descriptor ? Array.from(action.face_descriptor) : undefined,
    liveness: action.liveness,
    shift_template_id: action.shift_template_id,
    location_id: action.location_id,
    client_id: action.client_id,
    captured_image: action.photo_base64 ?? action.photo ?? null,
    method: action.method,
    offline_queued: action.offline_queued,
    synced: Boolean(action.synced),
    sync_attempts: action.sync_attempts,
    created_at: toAttendanceIsoString(action.created_at),
    truncated_clock_out: action.truncated_clock_out,
    original_clock_out_time: action.original_clock_out_time,
    time_drift_detected: action.time_drift_detected,
    time_drift_ms: action.time_drift_ms,
  };
}

function buildUnifiedAttendanceSnapshot(params: {
  employees: OfflineEmployee[];
  faceTemplates: OfflineFaceTemplate[];
  shiftTemplates: OfflineShiftTemplate[];
  geofences: OfflineGeofence[];
  pendingActions: OfflineClockAction[];
  syncMetadata?: OfflineSyncMetadata;
  dedicatedStatusMap?: Map<string, string>;
}): OfflineAttendanceData | null {
  const employees = params.employees
    .map((employee) => mapUnifiedAttendanceEmployee(employee, params.dedicatedStatusMap))
    .filter((employee) => employee.employee_id > 0);
  const faceTemplates = params.faceTemplates
    .map(mapUnifiedAttendanceFaceTemplate)
    .filter((template) => template.id > 0 && template.employee_id > 0);
  const shiftTemplates = params.shiftTemplates
    .map(mapUnifiedAttendanceShiftTemplate)
    .filter((shift) => shift.id > 0);
  const geofences = params.geofences
    .map(mapUnifiedAttendanceGeofence)
    .filter((geofence) => Boolean(geofence.id));
  const pendingLogs = params.pendingActions.map((action) => mapUnifiedPendingClockAction(action));

  const lastSync = params.syncMetadata?.last_full_sync ? toAttendanceIsoString(params.syncMetadata.last_full_sync) : null;
  const syncStatus: OfflineAttendanceData['sync_info']['status'] = lastSync ? 'success' : 'idle';

  const hasData = employees.length > 0
    || faceTemplates.length > 0
    || shiftTemplates.length > 0
    || geofences.length > 0
    || pendingLogs.length > 0
    || Boolean(lastSync);

  if (!hasData) {
    return null;
  }

  return {
    employees,
    face_templates: faceTemplates,
    shift_templates: shiftTemplates,
    geofences,
    pending_logs: pendingLogs,
    sync_info: {
      lastSync,
      status: syncStatus,
      employee_count: employees.length,
      template_count: faceTemplates.length,
    },
  };
}

async function loadUnifiedAttendanceSnapshot(clientId: number): Promise<OfflineAttendanceData | null> {
  const [employees, faceTemplates, shiftTemplates, geofences, pendingActions, syncMetadata, dedicatedStatusMap] = await Promise.all([
    offlineStorage.getAllEmployees(),
    offlineStorage.getAllFaceTemplates(),
    offlineStorage.getAllShiftTemplates(),
    offlineStorage.getAllGeofences(),
    offlineStorage.getPendingClockActions(),
    offlineStorage.getSyncMetadata(),
    attendanceStore.getAllClockStatusesMap(clientId).catch(() => new Map<string, string>()),
  ]);

  return buildUnifiedAttendanceSnapshot({
    employees,
    faceTemplates,
    shiftTemplates,
    geofences,
    pendingActions,
    syncMetadata,
    dedicatedStatusMap,
  });
}

function buildOfflineClockActionFromPendingLog(
  clientId: number,
  log: PendingAttendanceLog,
): Omit<OfflineClockAction, 'id'> {
  const normalizedAction = normalizeQueuedAttendanceAction(log.action_type || log.action);
  const descriptorValues = Array.isArray(log.face_descriptor)
    ? log.face_descriptor.map((value) => toFiniteNumber(value))
    : undefined;

  return {
    offline_id: generateAttendanceOfflineId(log.id),
    client_id: clientId,
    employee_id: toFiniteNumber(log.employee_id),
    action: normalizedAction,
    action_type: normalizedAction,
    timestamp: toAttendanceDate(log.timestamp),
    latitude: log.latitude,
    longitude: log.longitude,
    accuracy: log.accuracy,
    gps_accuracy: log.accuracy,
    wifi_ssid: log.wifi_ssid,
    wifi_bssid: log.wifi_bssid,
    signal_strength: log.signal_strength,
    beacon_uuid: log.beacon_uuid,
    template_id: log.template_id,
    confidence: log.confidence ?? log.face_match_score,
    face_descriptor: descriptorValues ? new Float32Array(descriptorValues) : undefined,
    liveness: log.liveness,
    shift_template_id: log.shift_template_id,
    location_id: log.location_id,
    photo_base64: log.captured_image ?? undefined,
    photo: log.captured_image ?? undefined,
    captured_image: log.captured_image ?? undefined,
    device_info: log.device_info ?? ((log.platform || log.device_type) ? { platform: log.platform || 'unknown', device_type: log.device_type } : null),
    method: log.method,
    offline_queued: true,
    synced: Boolean(log.synced),
    sync_attempts: log.sync_attempts ?? log.retry_count ?? 0,
    created_at: toAttendanceDate(log.created_at ?? log.timestamp),
    truncated_clock_out: log.truncated_clock_out,
    original_clock_out_time: log.original_clock_out_time,
    time_drift_detected: log.time_drift_detected,
    time_drift_ms: log.time_drift_ms,
  };
}

async function upsertUnifiedAttendancePendingLogRecord(
  clientId: number,
  log: PendingAttendanceLog,
): Promise<void> {
  const normalizedAction = normalizeQueuedAttendanceAction(log.action_type || log.action);
  const normalizedLogId = typeof log.id === 'string' && log.id.trim()
    ? log.id
    : generateAttendanceOfflineId();
  const numericId = buildStablePendingLogNumericId(normalizedLogId);

  await attendanceStore.upsertRecord('clock_actions', clientId, numericId, {
    id: numericId,
    offline_id: normalizedLogId,
    legacy_pending_log_id: normalizedLogId,
    client_id: clientId,
    employee_id: toFiniteNumber(log.employee_id),
    action: normalizedAction,
    action_type: normalizedAction,
    timestamp: toAttendanceIsoString(log.timestamp),
    latitude: typeof log.latitude === 'number' ? log.latitude : undefined,
    longitude: typeof log.longitude === 'number' ? log.longitude : undefined,
    accuracy: typeof log.accuracy === 'number' ? log.accuracy : undefined,
    gps_accuracy: typeof log.accuracy === 'number' ? log.accuracy : undefined,
    wifi_ssid: typeof log.wifi_ssid === 'string' ? log.wifi_ssid : undefined,
    wifi_bssid: typeof log.wifi_bssid === 'string' ? log.wifi_bssid : undefined,
    signal_strength: typeof log.signal_strength === 'number' ? log.signal_strength : undefined,
    beacon_uuid: typeof log.beacon_uuid === 'string' ? log.beacon_uuid : undefined,
    template_id: toOptionalFiniteNumber(log.template_id),
    confidence: typeof log.confidence === 'number' ? log.confidence : undefined,
    face_descriptor: Array.isArray(log.face_descriptor) ? log.face_descriptor : undefined,
    liveness: log.liveness && typeof log.liveness === 'object' ? log.liveness : undefined,
    shift_template_id: toOptionalFiniteNumber(log.shift_template_id),
    location_id: toOptionalFiniteNumber(log.location_id),
    photo_base64: typeof log.captured_image === 'string' ? log.captured_image : undefined,
    photo: typeof log.captured_image === 'string' ? log.captured_image : undefined,
    captured_image: typeof log.captured_image === 'string' ? log.captured_image : undefined,
    device_info: log.device_info ?? undefined,
    offline_queued: log.offline_queued !== false,
    synced: Boolean(log.synced),
    sync_attempts: toFiniteNumber(log.sync_attempts ?? log.retry_count),
    created_at: toAttendanceIsoString(log.created_at ?? log.timestamp),
  }, {
    status: log.synced ? 'synced' : 'pending',
    syncStatus: log.synced ? 'synced' : 'pending',
  });
}

async function persistUnifiedAttendanceSnapshot(
  clientId: number,
  snapshot: OfflineAttendanceData,
): Promise<void> {
  await hydrateOfflineAttendanceCache(clientId, {
    employees: snapshot.employees,
    face_templates: snapshot.face_templates,
    shift_templates: snapshot.shift_templates,
    geofences: snapshot.geofences,
  });

  await Promise.allSettled(
    (snapshot.employees || [])
      .filter((employee) => Boolean(employee?.employee_id) && Boolean(employee?.current_status))
      .map(async (employee) => {
        const normalizedStatus = employee.current_status;
        if (normalizedStatus !== 'clocked_in' && normalizedStatus !== 'clocked_out' && normalizedStatus !== 'on_break') {
          return;
        }

        await attendanceStore.setClockStatus(
          clientId,
          toFiniteNumber(employee.employee_id),
          normalizedStatus,
        );
      }),
  );

  if (Array.isArray(snapshot.pending_logs) && snapshot.pending_logs.length > 0) {
    await Promise.allSettled(
      snapshot.pending_logs.map((log) => upsertUnifiedAttendancePendingLogRecord(clientId, log)),
    );
  }

  if (snapshot.sync_info?.lastSync) {
    await offlineStorage.markFullSyncComplete();
  }
}

function normalizeStoredWorkItemChanges(
  value: unknown,
): Record<string, { old: unknown; new: unknown }> | undefined {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return undefined;
  }

  const changeEntries = Object.entries(value as Record<string, unknown>)
    .filter(([, change]) => change && typeof change === 'object' && !Array.isArray(change))
    .map(([key, change]) => {
      const typedChange = change as Record<string, unknown>;
      return [
        key,
        {
          old: typedChange.old,
          new: typedChange.new,
        },
      ] as const;
    });

  return changeEntries.length > 0 ? Object.fromEntries(changeEntries) : undefined;
}

function normalizeStoredWorkItemRecord(value: unknown): StoredOfflineWorkItem | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }

  const record = value as Record<string, unknown>;
  const nestedData = record.data && typeof record.data === 'object' && !Array.isArray(record.data)
    ? record.data as Record<string, unknown>
    : {};

  const resolvedId = record.id ?? nestedData.id;
  const resolvedItemType = record.item_type ?? nestedData.item_type;

  if ((typeof resolvedId !== 'string' && typeof resolvedId !== 'number') || typeof resolvedItemType !== 'string' || !resolvedItemType.trim()) {
    return null;
  }

  const resolvedTitle = record.title ?? nestedData.title;
  const resolvedStatus = record.status ?? nestedData.status;
  const resolvedSyncStatus = record.sync_status;

  return {
    ...record,
    id: String(resolvedId),
    item_type: resolvedItemType,
    item_number: typeof record.item_number === 'string' ? record.item_number : undefined,
    title: typeof resolvedTitle === 'string' ? resolvedTitle : '',
    status: typeof resolvedStatus === 'string' ? resolvedStatus : 'pending',
    data: {
      ...nestedData,
      id: nestedData.id ?? String(resolvedId),
      item_type: nestedData.item_type ?? resolvedItemType,
      ...(resolvedTitle !== undefined ? { title: nestedData.title ?? resolvedTitle } : {}),
      ...(resolvedStatus !== undefined ? { status: nestedData.status ?? resolvedStatus } : {}),
    },
    changes: normalizeStoredWorkItemChanges(record.changes),
    sync_status: resolvedSyncStatus === 'cached'
      || resolvedSyncStatus === 'modified'
      || resolvedSyncStatus === 'pending_create'
      || resolvedSyncStatus === 'pending_update'
      ? resolvedSyncStatus
      : 'modified',
  } as StoredOfflineWorkItem;
}

function getStoredWorkItemStatus(value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined;
}

async function upsertUnifiedWorkItemCache(
  clientId: number,
  workItem: StoredOfflineWorkItem,
  syncStatus: 'synced' | 'pending' = 'synced',
): Promise<void> {
  await workItemsStore.upsertRecord('work_items', clientId, workItem, {
    status: getStoredWorkItemStatus(workItem.status),
    syncStatus,
  });
}

async function bulkUpsertUnifiedWorkItemCache(
  clientId: number,
  workItems: StoredOfflineWorkItem[],
  syncStatus: 'synced' | 'pending' = 'synced',
): Promise<void> {
  if (workItems.length === 0) {
    return;
  }

  await workItemsStore.bulkUpsertRecords('work_items', clientId, workItems, (item) => ({
    status: getStoredWorkItemStatus(item.status),
    syncStatus,
  }));
}

async function listUnifiedCachedWorkItems(clientId: number): Promise<StoredOfflineWorkItem[]> {
  const items = await workItemsStore.listRecords<unknown>('work_items', clientId);
  return items
    .map((item) => normalizeStoredWorkItemRecord(item))
    .filter((item): item is StoredOfflineWorkItem => item !== null);
}

async function listUnifiedModifiedWorkItems(clientId: number): Promise<StoredOfflineWorkItem[]> {
  const items = await workItemsStore.listRecordsBySyncStatus<unknown>('work_items', clientId, 'pending');
  return items
    .map((item) => normalizeStoredWorkItemRecord(item))
    .filter((item): item is StoredOfflineWorkItem => item !== null);
}

export function getOperationsBreakdownFromMasterStats(masterStats: MasterSyncStats): MasterSyncOperationsBreakdown {
  const pendingChildEntries = masterStats.pendingChildEntries || 0;
  const pendingFormSubmissions = masterStats.pendingFormSubmissions || 0;
  const pendingSmartTaskActions = masterStats.pendingSmartTaskActions || 0;
  const pendingInventoryMovements = masterStats.pendingInventoryMovements || 0;
  const pendingStockCountActions = masterStats.pendingStockCountActions || 0;
  const pendingWorkItemUploads = masterStats.pendingWorkItemUploads || 0;
  const pendingWorkItems = Math.max(
    0,
    (masterStats.pendingWorkItems || 0)
    - pendingChildEntries
    - pendingFormSubmissions
    - pendingSmartTaskActions
    - pendingInventoryMovements
    - pendingStockCountActions
  );

  return {
    pendingWorkItems,
    pendingWorkItemUploads,
    pendingChildEntries,
    pendingFormSubmissions,
    pendingSmartTaskActions,
    pendingInventoryMovements,
    pendingStockCountActions,
  };
}

export function getTotalPendingCountFromMasterStats(masterStats: MasterSyncStats): number {
  const operations = getOperationsBreakdownFromMasterStats(masterStats);

  return (masterStats.pendingAttendanceLogs || 0)
    + (masterStats.pendingMessages || 0)
    + (masterStats.pendingEmails || 0)
    + operations.pendingWorkItems
    + operations.pendingWorkItemUploads
    + operations.pendingChildEntries
    + operations.pendingFormSubmissions
    + operations.pendingSmartTaskActions
    + operations.pendingInventoryMovements
    + operations.pendingStockCountActions;
}

function normalizeFaceDescriptor(descriptor: unknown): Float32Array {
  try {
    if (descriptor instanceof Float32Array) {
      return descriptor;
    }

    if (Array.isArray(descriptor)) {
      return new Float32Array(descriptor.map((value) => Number(value) || 0));
    }

    if (typeof descriptor === 'string') {
      const trimmed = descriptor.trim();

      if (trimmed.startsWith('[')) {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) {
          return new Float32Array(parsed.map((value) => Number(value) || 0));
        }
      }

      if (/^[A-Za-z0-9+/]*={0,2}$/.test(trimmed)) {
        const binary = atob(trimmed);
        const bytes = new Uint8Array(binary.length);
        for (let index = 0; index < binary.length; index += 1) {
          bytes[index] = binary.charCodeAt(index);
        }
        return new Float32Array(bytes.buffer);
      }
    }
  } catch (error) {
    console.warn('[MasterSync] Failed to normalize face descriptor:', error);
  }

  return new Float32Array(128);
}

async function hydrateOfflineAttendanceCache(clientId: number, payload: {
  employees?: any[];
  face_templates?: any[];
  shift_templates?: any[];
  geofences?: any[];
}): Promise<void> {
  const employees = Array.isArray(payload.employees) ? payload.employees : [];
  const faceTemplates = Array.isArray(payload.face_templates) ? payload.face_templates : [];
  const shiftTemplates = Array.isArray(payload.shift_templates) ? payload.shift_templates : [];
  const geofences = Array.isArray(payload.geofences) ? payload.geofences : [];

  // Personal-mode payloads carry permissions; kiosk payloads don't. Preserve
  // the last-known values when the incoming record has none so a kiosk sync
  // can't erase a known app_clock_enabled=false.
  const existingEmployeesById = new Map<number, { user_id?: number | null; app_clock_enabled?: boolean; allow_enrollment_recapture?: boolean }>();
  try {
    for (const existing of await offlineStorage.getAllEmployees()) {
      existingEmployeesById.set(Number(existing.id), {
        user_id: existing.user_id ?? null,
        app_clock_enabled: existing.app_clock_enabled,
        allow_enrollment_recapture: existing.allow_enrollment_recapture,
      });
    }
  } catch {
    // Non-fatal — records just won't inherit prior permissions.
  }

  const mappedEmployees = employees
    .map((employee: any) => {
      const rawUserId = employee.user_id;
      const normalizedUserId = rawUserId != null ? Number(rawUserId) : NaN;
      const resolvedId = Number(employee.employee_id || employee.id);
      const existingPerms = existingEmployeesById.get(resolvedId);
      return {
        id: resolvedId,
        employee_id: resolvedId,
        // Only persist a real user_id — a fabricated value could match the
        // wrong user to this employee offline. An explicit null is honoured
        // (employee unlinked); only an absent key keeps the stored link.
        user_id: 'user_id' in (employee as Record<string, unknown>)
          ? (Number.isFinite(normalizedUserId) && normalizedUserId > 0 ? normalizedUserId : null)
          : (existingPerms?.user_id ?? null),
        client_id: Number(employee.client_id || clientId),
        employee_number: employee.employee_number || '',
        first_name: employee.first_name || '',
        last_name: employee.last_name || '',
        email: employee.email,
        location_flexible: Boolean(employee.location_flexible),
        shift_template_id: employee.shift_template_id ? Number(employee.shift_template_id) : undefined,
        // Tri-state permissions: keep explicit booleans, fall back to the
        // previously-stored value, leave undefined when truly unknown.
        app_clock_enabled: typeof employee.app_clock_enabled === 'boolean'
          ? employee.app_clock_enabled
          : existingPerms?.app_clock_enabled,
        allow_enrollment_recapture: typeof employee.allow_enrollment_recapture === 'boolean'
          ? employee.allow_enrollment_recapture
          : existingPerms?.allow_enrollment_recapture,
        active_days: Array.isArray(employee.active_days) ? employee.active_days : [],
        current_status: employee.current_status || 'clocked_out',
        last_status_update: employee.last_status_update ? new Date(employee.last_status_update) : undefined,
        synced_at: new Date(),
      };
    })
    .filter((employee) => Number.isFinite(employee.id) && employee.id > 0);

  const mappedTemplates = faceTemplates
    .map((template: any) => ({
      id: Number(template.id),
      client_id: Number(template.client_id || clientId),
      employee_id: Number(template.employee_id),
      descriptor: normalizeFaceDescriptor(template.descriptor),
      quality_score: Number(template.quality_score || 0),
      confidence: Number(template.confidence || 0),
      is_active: template.is_active !== false,
      created_at: template.created_at ? new Date(template.created_at) : new Date(),
      synced_at: new Date(),
      descriptor_hash: template.descriptor_hash,
      enrolled_at: template.enrolled_at,
      server_signature: template.server_signature,
      version: Number(template.version || 1),
    }))
    .filter((template) => Number.isFinite(template.id) && template.id > 0 && Number.isFinite(template.employee_id) && template.employee_id > 0);

  if (mappedEmployees.length > 0) {
    await offlineStorage.saveEmployees(mappedEmployees);
  }

  if (mappedTemplates.length > 0) {
    await offlineStorage.saveFaceTemplates(mappedTemplates);
  }

  if (shiftTemplates.length > 0) {
    await offlineStorage.saveShiftTemplates(shiftTemplates.map((shift: any) => ({
      ...shift,
      id: Number(shift.id),
      client_id: Number(shift.client_id || clientId),
      synced_at: new Date(),
    })));
  }

  if (geofences.length > 0) {
    await offlineStorage.saveGeofences(geofences.map((geofence: any) => ({
      ...geofence,
      client_id: Number(geofence.client_id || clientId),
      synced_at: new Date(),
    })));
  }
}

function normalizeAttendanceOfflinePayload(data: any) {
  // Personal and single modes return a per-requester permissions block;
  // stamp it onto the employee record so the values persist into the
  // offline store (kiosk payloads have no permissions block — the fields
  // stay undefined, i.e. unknown rather than disabled).
  const permissions = data?.permissions && typeof data.permissions === 'object'
    ? data.permissions as Record<string, unknown>
    : null;
  const appClockEnabled = typeof permissions?.app_clock_enabled === 'boolean'
    ? permissions.app_clock_enabled as boolean
    : undefined;
  const allowEnrollmentRecapture = typeof permissions?.allow_enrollment_recapture === 'boolean'
    ? permissions.allow_enrollment_recapture as boolean
    : undefined;

  const personalEmployee = data?.employee_info ? {
    id: Number(data.employee_info.id),
    employee_id: Number(data.employee_info.id),
    user_id: data.employee_info.user_id != null ? Number(data.employee_info.user_id) : null,
    employee_number: data.employee_info.employee_number,
    first_name: String(data.employee_info.name || '').split(' ')[0] || '',
    last_name: String(data.employee_info.name || '').split(' ').slice(1).join(' ') || '',
    email: '',
    location_flexible: Boolean(data.employee_info.location_flexible),
    shift_template_id: data.shift_info?.template?.id ? Number(data.shift_info.template.id) : undefined,
    app_clock_enabled: appClockEnabled,
    allow_enrollment_recapture: allowEnrollmentRecapture,
    current_status: data.current_status || 'clocked_out',
    last_status_update: new Date().toISOString(),
  } : null;

  return {
    employees: Array.isArray(data?.employees)
      ? (appClockEnabled === undefined && allowEnrollmentRecapture === undefined
        ? data.employees
        : (data.employees as Record<string, unknown>[]).map((employee) => ({
          ...employee,
          app_clock_enabled: appClockEnabled,
          allow_enrollment_recapture: allowEnrollmentRecapture,
        })))
      : personalEmployee ? [personalEmployee] : [],
    faceTemplates: Array.isArray(data?.face_templates) ? data.face_templates : [],
    shiftTemplates: Array.isArray(data?.shift_templates)
      ? data.shift_templates
      : data?.shift_info?.template ? [data.shift_info.template] : [],
    geofences: Array.isArray(data?.geofences)
      ? data.geofences
      : Array.isArray(data?.geofence_config?.geofences) ? data.geofence_config.geofences : [],
  };
}

// ==================== MASTER SYNC CLASS ====================

class MasterSync {
  private config: MasterSyncConfig | null = null;
  private isSyncing = false;
  private syncAbortController: AbortController | null = null;
  private isPreparingOffline = false;
  private offlinePreparationAbortController: AbortController | null = null;
  private listeners: Set<(progress: SyncProgress) => void> = new Set();
  private lastProgress: SyncProgress = { stage: 'idle', module: null, message: '', current: 0, total: 0 };
  private lastConfigKey: string | null = null;
  private lastFullSyncAt: Date | null = null;
  private lastSyncResultsSnapshot: SyncResult[] = [];
  private lastOperationsSyncStepResults: OperationSyncStepResult[] = [];
  private isBgSmartTaskSyncRunning = false;
  private pendingConflicts: ConflictEntry[] = [];

  private isSmartTaskColdStartDiagnosticsEnabled(): boolean {
    if (typeof window === 'undefined') {
      return false;
    }

    if (typeof process !== 'undefined' && process.env.NODE_ENV === 'development') {
      return true;
    }

    try {
      const host = window.location.hostname.toLowerCase();
      return host === 'localhost'
        || host === '127.0.0.1'
        || host.startsWith('dev-') || host === 'dev.totalaccess.co.za'
        || host.includes('dev-app') || host === 'dev.totalaccess.co.za'
        || window.localStorage.getItem('debug_smarttask_coldstart') === 'true';
    } catch {
      return false;
    }
  }

  private getSmartTaskColdStartNow(): number {
    if (typeof performance !== 'undefined' && typeof performance.now === 'function') {
      return performance.now();
    }

    return Date.now();
  }

  private writeSmartTaskColdStartLog(tag: string, msg: string, data?: Record<string, unknown>): void {
    if (!this.isSmartTaskColdStartDiagnosticsEnabled() || typeof window === 'undefined') {
      return;
    }

    const coldStartLogger = (window as typeof window & {
      __coldStartLog?: (tag: string, msg: string, data?: unknown) => void;
    }).__coldStartLog;

    if (typeof coldStartLogger !== 'function') {
      return;
    }

    try {
      coldStartLogger(`SMARTTASK_${tag}`, msg, data);
    } catch {
      // Diagnostics must never interfere with runtime behavior.
    }
  }

  private writeSmartTaskColdStartDuration(tag: string, msg: string, startedAt: number, data?: Record<string, unknown>): void {
    this.writeSmartTaskColdStartLog(tag, msg, {
      ...data,
      durationMs: Math.max(0, Math.round(this.getSmartTaskColdStartNow() - startedAt)),
    });
  }

  private async ensureOperationsClientContext(clientIdOverride?: number | null): Promise<number> {
    const startedAt = this.getSmartTaskColdStartNow();
    const clientId = this.resolveClientId(clientIdOverride);
    await offlineWorkItems.handleClientSwitch(clientId).catch(() => null);
    this.writeSmartTaskColdStartDuration('MASTERSYNC_OPERATIONS_CONTEXT_READY', 'Resolved MasterSync operations client context', startedAt, {
      clientId,
    });
    return clientId;
  }

  private resolveClientId(clientIdOverride?: number | null): number {
    const resolvedClientId = Number(clientIdOverride ?? this.config?.clientId ?? 0);
    if (!Number.isFinite(resolvedClientId) || resolvedClientId <= 0) {
      throw new Error('MasterSync client context is not available');
    }

    return resolvedClientId;
  }

  private resolveUserId(userIdOverride?: number | null): number {
    const resolvedUserId = Number(userIdOverride ?? this.config?.userId ?? 0);
    if (!Number.isFinite(resolvedUserId) || resolvedUserId <= 0) {
      throw new Error('MasterSync user context is not available');
    }

    return resolvedUserId;
  }

  private getFleetInspectionQueueStorageKey(clientId: number): string {
    return `${LEGACY_FLEET_INSPECTION_QUEUE_KEY}:${clientId}`;
  }

  private getVehicleInspectionDraftStorageKey(vehicleId: string): string {
    return `${VEHICLE_INSPECTION_DRAFT_PREFIX}${vehicleId}`;
  }

  private getVehicleInspectionDraftEntityId(vehicleId: string): string {
    return `${VEHICLE_INSPECTION_DRAFT_ENTITY_PREFIX}${vehicleId}`;
  }

  private getLastVehicleInspectionSnapshotStorageKey(vehicleId: string): string {
    return `${LAST_VEHICLE_INSPECTION_SNAPSHOT_PREFIX}${vehicleId}`;
  }

  private getLastVehicleInspectionSnapshotEntityId(vehicleId: string): string {
    return `${LAST_VEHICLE_INSPECTION_SNAPSHOT_ENTITY_PREFIX}${vehicleId}`;
  }

  private getSmartTaskRoutePlanStorageKey(travelSessionId: string): string {
    return `${SMARTTASK_ROUTE_PLAN_PREFIX}${travelSessionId}`;
  }

  private getSmartTaskRoutePlanEntityId(travelSessionId: string): string {
    return `${SMARTTASK_ROUTE_PLAN_ENTITY_PREFIX}${travelSessionId}`;
  }

  private getSyncMetadataStorageKey(clientIdOverride?: number | null, userIdOverride?: number | null): string | null {
    const clientId = Number(clientIdOverride ?? this.config?.clientId ?? 0);
    const userId = Number(userIdOverride ?? this.config?.userId ?? 0);

    if (!Number.isFinite(clientId) || clientId <= 0 || !Number.isFinite(userId) || userId <= 0) {
      return null;
    }

    return `${MASTERSYNC_METADATA_STORAGE_PREFIX}:${userId}:${clientId}`;
  }

  private readLocalStorageJson<T>(key: string, fallback: T): T {
    if (typeof window === 'undefined') {
      return fallback;
    }

    try {
      const raw = window.localStorage.getItem(key);
      return raw ? JSON.parse(raw) as T : fallback;
    } catch {
      return fallback;
    }
  }

  private writeLocalStorageJson(key: string, value: unknown): void {
    if (typeof window === 'undefined') {
      return;
    }

    window.localStorage.setItem(key, JSON.stringify(value));
  }

  private removeLocalStorageKey(key: string): void {
    if (typeof window === 'undefined') {
      return;
    }

    window.localStorage.removeItem(key);
  }

  private getAppShellClientId(clientIdOverride?: number | null): number | null {
    const clientId = Number(clientIdOverride ?? this.config?.clientId ?? 0);
    return Number.isFinite(clientId) && clientId > 0 ? clientId : null;
  }

  private hydratePersistedSyncMetadata(): void {
    const storageKey = this.getSyncMetadataStorageKey();
    if (!storageKey || typeof window === 'undefined') {
      return;
    }

    const persisted = this.readLocalStorageJson<{
      lastFullSyncAt?: string | null;
      lastSyncResults?: SyncResult[];
      lastOperationsSyncStepResults?: OperationSyncStepResult[];
    } | null>(storageKey, null);

    if (!persisted) {
      this.lastFullSyncAt = null;
      this.lastSyncResultsSnapshot = [];
      this.lastOperationsSyncStepResults = [];
      return;
    }

    this.lastFullSyncAt = persisted.lastFullSyncAt ? new Date(persisted.lastFullSyncAt) : null;
    this.lastSyncResultsSnapshot = Array.isArray(persisted.lastSyncResults) ? persisted.lastSyncResults : [];
    this.lastOperationsSyncStepResults = Array.isArray(persisted.lastOperationsSyncStepResults)
      ? persisted.lastOperationsSyncStepResults
      : [];
  }

  private persistSyncMetadata(): void {
    const storageKey = this.getSyncMetadataStorageKey();
    if (!storageKey) {
      return;
    }

    this.writeLocalStorageJson(storageKey, {
      lastFullSyncAt: this.lastFullSyncAt?.toISOString() ?? null,
      lastSyncResults: this.lastSyncResultsSnapshot,
      lastOperationsSyncStepResults: this.lastOperationsSyncStepResults,
    });
  }

  private async persistAttendanceStatus(clientId: number, employeeId: number, status: AttendanceQueueStatus): Promise<void> {
    try {
      await attendanceStore.setClockStatus(clientId, employeeId, status);

      await offlineStorage.updateEmployeeStatus(employeeId, status).catch((offlineError) => {
        console.warn('[MasterSync] Failed to persist attendance status to offline storage:', offlineError);
      });
    } catch (statusError) {
      console.warn('[MasterSync] Failed to persist queued attendance status:', statusError);
    }
  }

  private getAttendanceOfflineMode(modeOverride?: 'personal' | 'kiosk' | 'single' | 'auto'): 'personal' | 'kiosk' | 'single' {
    if (!modeOverride || modeOverride === 'auto') {
      // Most devices run personal mode — only pull the full-employee kiosk
      // snapshot when SmartClock's template mode was explicitly switched to
      // kiosk (same key SmartClock's templateDownloadMode reads).
      let kioskPreferred = false;
      try {
        const savedMode = typeof window !== 'undefined'
          ? window.localStorage.getItem('smartclock_template_mode')
          : null;
        kioskPreferred = savedMode === 'kiosk' || savedMode === 'company';
      } catch {
        // non-fatal — fall through to the default
      }
      if (kioskPreferred) return 'kiosk';
      return isCapacitor() ? 'personal' : 'single';
    }

    return modeOverride;
  }

  private getSyncSignal(): AbortSignal | undefined {
    return this.syncAbortController?.signal;
  }

  private getOfflinePreparationSignal(): AbortSignal | undefined {
    return this.offlinePreparationAbortController?.signal;
  }

  private async downloadAttendanceSnapshot(
    modeOverride?: 'personal' | 'kiosk' | 'single' | 'auto',
    signalOverride?: AbortSignal,
  ): Promise<{
    downloadedEmployees: number;
    downloadedTemplates: number;
    downloadedShifts: number;
    downloadedGeofences: number;
    mode: 'personal' | 'kiosk' | 'single';
  }> {
    if (!this.config) {
      throw new Error('Not configured');
    }

    const { clientId } = this.config;
    const mode = this.getAttendanceOfflineMode(modeOverride);
    const endpoint = mode === 'single'
      ? '/api/v1/time-attendance/offline-status'
      : `/api/v1/time-attendance/offline-status?mode=${mode}`;

    // Fetch with 401 → token refresh → retry logic.
    // masterSync stores authToken in config, which can become stale if the
    // token was refreshed by apiService/portalSessionManager while the tab
    // was backgrounded. On 401, refresh via apiService and retry with the
    // fresh token.
    const doFetch = async (token: string): Promise<Response> => {
      return fetch(buildApiUrl(endpoint), {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        signal: signalOverride ?? this.getSyncSignal(),
      });
    };

    let response = await doFetch(this.config.authToken);

    // 401 → attempt token refresh → retry once with fresh token
    if (response.status === 401) {
      debugLog('[MasterSync]', 'Attendance snapshot got 401 — attempting token refresh...');
      const refreshed = await apiService.attemptTokenRefresh();
      if (refreshed) {
        const freshTokens = authService.getTokens();
        const freshToken = freshTokens?.access_token ?? freshTokens?.accessToken;
        if (freshToken) {
          this.updateAuthToken(freshToken);
          response = await doFetch(freshToken);
        }
      }
    }

    if (!response.ok) {
      let errorMessage = 'Failed to download attendance data';
      try {
        const error = await response.json();
        errorMessage = error?.error || errorMessage;
      } catch {
        // ignore json parse issues
      }
      throw new Error(errorMessage);
    }

    const data = await response.json();
    if (!data?.success) {
      throw new Error(data?.error || 'Failed to download attendance data');
    }

    // Persist the server-resolved face match threshold so verifyFaceOffline
    // applies the same floor while offline (system-admin configured per client).
    if (data.face_match_threshold != null) {
      await setCachedFaceMatchThreshold(clientId, Number(data.face_match_threshold)).catch((e) => {
        console.warn('[MasterSync] Failed to cache face match threshold:', e);
      });
    }

    const persisted = await this.persistOfflineStatusPayload(clientId, data);

    return {
      downloadedEmployees: persisted.mergedEmployees.length,
      downloadedTemplates: persisted.normalized.faceTemplates.length,
      downloadedShifts: persisted.normalized.shiftTemplates.length,
      downloadedGeofences: persisted.normalized.geofences.length,
      mode,
    };
  }

  /**
   * Normalize an /offline-status response and persist it into the offline
   * attendance stores, preserving locally-known clock statuses. Shared by the
   * scheduled download path and SmartClock's bootstrap-persist path.
   */
  private async persistOfflineStatusPayload(clientId: number, data: any, options?: {
    markFullSync?: boolean;
  }): Promise<{
    mergedEmployees: any[];
    normalized: ReturnType<typeof normalizeAttendanceOfflinePayload>;
  }> {
    const normalized = normalizeAttendanceOfflinePayload(data);

    const dedicatedStatusMap = await attendanceStore.getAllClockStatusesMap(clientId).catch(() => new Map<string, string>());
    const existingData = await loadUnifiedAttendanceSnapshot(clientId).catch(() => null);
    const fullDataStatusMap = new Map<string, string>();
    if (existingData?.employees) {
      for (const employee of existingData.employees) {
        if (employee.employee_id && employee.current_status) {
          fullDataStatusMap.set(String(employee.employee_id), employee.current_status);
        }
      }
    }

    const mergedEmployees = normalized.employees.map((employee: any) => {
      const employeeId = String(employee.employee_id || employee.id);
      const dedicatedStatus = dedicatedStatusMap.get(employeeId);
      const existingStatus = fullDataStatusMap.get(employeeId);

      return {
        ...employee,
        current_status: dedicatedStatus || existingStatus || employee.current_status || 'clocked_out',
      };
    });

    const attendanceData: OfflineAttendanceData = {
      employees: mergedEmployees,
      face_templates: normalized.faceTemplates,
      shift_templates: normalized.shiftTemplates,
      geofences: normalized.geofences,
      pending_logs: [],
      sync_info: {
        // Only a scheduled download counts as a "full sync" — a personal
        // bootstrap refresh must not unlock last_full_sync reporting.
        lastSync: options?.markFullSync === false ? null : new Date().toISOString(),
        status: 'success',
        employee_count: mergedEmployees.length,
        template_count: normalized.faceTemplates.length,
      },
    };

    const pendingActions = await offlineStorage.getPendingClockActions().catch(() => []);
    attendanceData.pending_logs = pendingActions
      .map((action) => mapUnifiedPendingClockAction(action))
      .filter((log) => !log.synced);

    await persistUnifiedAttendanceSnapshot(clientId, attendanceData).catch((snapshotError) => {
      console.warn('[MasterSync] Failed to persist unified attendance snapshot:', snapshotError);
    });

    return { mergedEmployees, normalized };
  }

  /**
   * Persist an already-fetched /offline-status API response (e.g. SmartClock's
   * personal bootstrap) so an online visit always seeds the offline cache —
   * even when the scheduled download path was skipped or throttled.
   */
  async persistAttendanceBootstrap(params: {
    clientId?: number | null;
    data: unknown;
  }): Promise<void> {
    const payload = params.data as { success?: boolean } | null;
    if (!payload?.success) {
      return;
    }
    const clientId = this.resolveClientId(params.clientId);
    await this.persistOfflineStatusPayload(clientId, params.data, { markFullSync: false });
  }

  private async cacheWorkItemsForOffline(workItems: Array<OperationalFormWorkItemTarget & Record<string, any>>): Promise<number> {
    if (!this.config || workItems.length === 0) {
      return 0;
    }

    await this.cacheOperationsWorkItems(workItems, this.config.clientId);
    await this.cacheOperationsWorkItems(workItems, this.config.clientId, SMART_TASK_SOURCE_CACHE_KEY);
    return workItems.length;
  }

  /**
   * Fetch with retry for transient upstream failures.
   *
   * 502/503/504 responses often originate at the edge (cloudflared → Next.js
   * keep-alive race: Node closes idle sockets after 5s while the tunnel pools
   * them for up to 90s), meaning the request never reached the app — safe to
   * retry for idempotent reads.
   */
  private async fetchWithTransientRetry(
    url: string,
    init: RequestInit,
    retries = 2,
    baseDelayMs = 750,
  ): Promise<Response> {
    const signal = init.signal ?? null;
    let lastError: unknown = null;

    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        const response = await fetch(url, init);
        if (![502, 503, 504].includes(response.status) || attempt >= retries) {
          return response;
        }
        lastError = new Error(`Transient upstream error (${response.status})`);
      } catch (error: any) {
        if (error?.name === 'AbortError' || signal?.aborted || attempt >= retries) {
          throw error;
        }
        lastError = error;
      }

      // Abortable backoff delay between attempts.
      await new Promise<void>((resolve) => {
        let timer: ReturnType<typeof setTimeout> | null = null;
        const onAbort = () => {
          if (timer !== null) {
            clearTimeout(timer);
          }
          resolve();
        };
        timer = setTimeout(() => {
          signal?.removeEventListener('abort', onAbort);
          resolve();
        }, baseDelayMs * (attempt + 1));
        signal?.addEventListener('abort', onAbort, { once: true });
      });

      if (signal?.aborted) {
        const abortError = new Error('Request aborted');
        abortError.name = 'AbortError';
        throw abortError;
      }
    }

    throw lastError instanceof Error ? lastError : new Error('Request failed');
  }

  private async fetchFleetVehiclesForOffline(
    signalOverride?: AbortSignal,
  ): Promise<Array<Record<string, any>>> {
    if (!this.config) {
      throw new Error('Not configured');
    }

    const signal = signalOverride ?? this.getOfflinePreparationSignal();
    const response = await this.fetchWithTransientRetry(buildApiUrl('/api/v1/work-items/fleet'), {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${this.config.authToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ action: 'getVehicles' }),
      signal,
    });

    if (!response.ok) {
      throw new Error(`Failed to load fleet vehicles (${response.status})`);
    }

    const data = await response.json().catch(() => null);
    const vehicles = Array.isArray(data?.data?.vehicles)
      ? data.data.vehicles
      : Array.isArray(data?.vehicles)
        ? data.vehicles
        : [];

    await this.cacheFleetVehicles(vehicles, this.config.clientId);
    return vehicles;
  }

  private async fetchAssignedWorkItemsForOffline(
    signalOverride?: AbortSignal,
  ): Promise<Array<OperationalFormWorkItemTarget & Record<string, any>>> {
    if (!this.config) {
      throw new Error('Not configured');
    }

    const authHeaders = { 'Authorization': `Bearer ${this.config.authToken}` };
    const signal = signalOverride ?? this.getOfflinePreparationSignal();
    // Offline preparation only fetches today's open items (fast mode).
    // Closed items are retrieved online on-demand when the user switches to the Closed tab.
    const sourceParams = buildSmartTaskAssignedSourceParams({
      config: {
        features: { viewAllJobs: false },
      } as any,
      demoTrainingOnly: false,
      mode: 'fast',
    });

    const response = await this.fetchWithTransientRetry(
      buildApiUrl(`/api/v1/work-items?${sourceParams.toString()}`),
      { headers: authHeaders, signal },
    );

    if (!response.ok) {
      let errorMessage = 'Failed to load assigned work items';
      try {
        const error = await response.json();
        errorMessage = error?.error || error?.message || errorMessage;
      } catch {
        // ignore json parse issues
      }
      throw new Error(errorMessage);
    }

    const data = await response.json();
    if (!data?.success) {
      throw new Error(data?.error || data?.message || 'Failed to load assigned work items');
    }

    const items = Array.isArray(data?.items)
      ? data.items as Array<OperationalFormWorkItemTarget & Record<string, any>>
      : Array.isArray(data?.data)
        ? data.data as Array<OperationalFormWorkItemTarget & Record<string, any>>
        : [];

    const dedupedItems = Array.from(
      new Map<string, OperationalFormWorkItemTarget & Record<string, any>>(
        items
          .filter((item): item is OperationalFormWorkItemTarget & Record<string, any> => Boolean(item?.id && !item?.deleted_at))
          .map((item) => [String(item.id), item]),
      ).values(),
    );

    return dedupedItems;
  }

  // ==================== CONFIGURATION ====================

  /**
   * Configure the sync service with user context
   * IDEMPOTENT: Only logs/configures if user/client changed
   */
  configure(config: MasterSyncConfig): void {
    // Create a unique key for this user/client combination
    const configKey = `${config.userId}:${config.clientId}`;

    // Skip if already configured for same user/client (idempotent)
    if (this.lastConfigKey === configKey && this.config) {
      // Silently update callbacks without re-logging
      this.config.onProgress = config.onProgress;
      this.config.onComplete = config.onComplete;
      this.config.onError = config.onError;
      return;
    }

    this.config = config;
    this.lastConfigKey = configKey;
    this.hydratePersistedSyncMetadata();
  }

  /**
   * Check if sync is configured for a user
   */
  isConfigured(): boolean {
    return this.config !== null && this.lastConfigKey !== null;
  }

  /**
   * Reset configuration (e.g., on logout)
   */
  reset(): void {
    const existingStorageKey = this.getSyncMetadataStorageKey();
    this.config = null;
    this.lastConfigKey = null;
    this.isSyncing = false;
    this.isPreparingOffline = false;
    if (this.syncAbortController) {
      this.syncAbortController.abort();
      this.syncAbortController = null;
    }
    if (this.offlinePreparationAbortController) {
      this.offlinePreparationAbortController.abort();
      this.offlinePreparationAbortController = null;
    }
    if (existingStorageKey) {
      this.removeLocalStorageKey(existingStorageKey);
    }
    this.lastFullSyncAt = null;
    this.lastSyncResultsSnapshot = [];
    this.lastOperationsSyncStepResults = [];
    this.lastProgress = { stage: 'idle', module: null, message: '', current: 0, total: 0 };
    debugLog('[MasterSync]', 'Reset');
  }

  /**
   * Update auth token (e.g., after refresh)
   */
  updateAuthToken(token: string): void {
    if (this.config) {
      this.config.authToken = token;
    }
  }

  /**
   * Update module access (e.g., after navigation data load)
   */
  updateModuleAccess(moduleAccess: ModuleAccess): void {
    if (this.config) {
      this.config.moduleAccess = moduleAccess;
      debugLog('[MasterSync]', 'Module access updated:', moduleAccess);
    }
  }

  // ==================== PROGRESS TRACKING ====================

  /**
   * Subscribe to progress updates
   */
  subscribe(callback: (progress: SyncProgress) => void): () => void {
    this.listeners.add(callback);
    // Immediately send current progress
    callback(this.lastProgress);
    return () => {
      this.listeners.delete(callback);
    };
  }

  private updateProgress(progress: Partial<SyncProgress>): void {
    this.lastProgress = { ...this.lastProgress, ...progress };
    this.listeners.forEach(cb => cb(this.lastProgress));
    this.config?.onProgress?.(this.lastProgress);
  }

  // ==================== MAIN SYNC ====================

  /**
   * Trigger a full sync of all modules
   */
  async triggerFullSync(): Promise<SyncResult[]> {
    if (!this.config) {
      console.error('[MasterSync] Not configured');
      return [];
    }

    if (this.isSyncing) {
      debugLog('[MasterSync]', 'Sync already in progress');
      return [];
    }

    if (this.isPreparingOffline) {
      debugLog('[MasterSync]', 'Offline preparation already in progress');
      return [];
    }

    if (!networkState.isOnline()) {
      debugLog('[MasterSync]', 'Offline - cannot sync');
      this.updateProgress({ stage: 'idle', message: 'No network connection' });
      return [];
    }

    this.isSyncing = true;
    this.syncAbortController = new AbortController();

    const results: SyncResult[] = [];
    const startTime = Date.now();

    try {
      this.updateProgress({ stage: 'checking', module: null, message: 'Checking pending items...' });

      // Get pending counts for each module
      const stats = await this.getStats();
      debugLog('[MasterSync]', 'Pending items:', stats);

      // Sync each module in priority order
      for (const module of SYNC_PRIORITIES) {
        if (!this.shouldSyncModule(module)) {
          debugLog('[MasterSync]', `Skipping ${module} - no access`);
          continue;
        }

        try {
          const result = await this.syncModule(module);
          results.push(result);
        } catch (error: any) {
          if (!isSilentFetchError(error)) console.error(`[MasterSync] ${module} sync failed:`, error);
          results.push({
            success: false,
            module,
            uploaded: 0,
            downloaded: 0,
            failed: 0,
            errors: [error.message],
            durationMs: 0
          });
        }
      }

      // Drain publish retry queue after module syncs
      try {
        const { clientId, authToken } = this.config;
        const retryResult = await this.drainPublishRetryQueue({ clientId, authToken });
        if (retryResult.retried > 0) {
          debugLog('[MasterSync]', 'Publish retry queue results: ' + JSON.stringify(retryResult));
        }
      } catch (retryError) {
        console.warn('[MasterSync] Publish retry queue drain failed:', retryError);
      }

      // Flush Tauri monitoring data (SQLite offline buffer → server)
      try {
        await this.syncMonitoringData();
      } catch (monitoringError) {
        console.warn('[MasterSync] Monitoring data flush failed:', monitoringError);
      }

      // Run daily storage cleanup after successful sync (lightweight, non-blocking on failure)
      try {
        if (this.shouldRunCleanup()) {
          await this.runStorageCleanup();
        }
      } catch (cleanupError) {
        console.warn('[MasterSync] Storage cleanup failed:', cleanupError);
      }

      this.updateProgress({
        stage: 'complete',
        module: null,
        message: 'Sync complete',
        current: results.filter(r => r.success).length,
        total: results.length
      });

      this.lastFullSyncAt = new Date();
      this.lastSyncResultsSnapshot = results;
      this.persistSyncMetadata();

      this.config?.onComplete?.(results);

    } catch (error: any) {
      if (!isSilentFetchError(error)) console.error('[MasterSync] Full sync failed:', error);
      this.updateProgress({
        stage: 'error',
        message: error.message || 'Sync failed',
        error: error.message
      });
      this.config?.onError?.(error);

    } finally {
      this.isSyncing = false;
      this.syncAbortController = null;

      // Clear progress after a delay
      setTimeout(() => {
        if (!this.isSyncing) {
          this.updateProgress({ stage: 'idle', message: '', current: 0, total: 0 });
        }
      }, 3000);
    }

    debugLog('[MasterSync]', `Full sync completed in ${Date.now() - startTime}ms`);
    return results;
  }

  /**
   * Flush Tauri desktop monitoring data from local SQLite to server.
   * Only runs on Tauri desktop platforms.  Invokes the Rust command
   * `flush_pending_monitoring_data` which reads pending app usage,
   * activity samples, and screenshots from SQLite and POSTs them to
   * the monitoring API.  Data remains in SQLite on failure for retry.
   */
  private async syncMonitoringData(): Promise<void> {
    if (!isTauri()) return;

    try {
      const tauri = (window as any).__TAURI_INTERNALS__;
      if (!tauri?.invoke) {
        debugLog('[MasterSync]', 'Tauri invoke not available — skipping monitoring flush');
        return;
      }

      debugLog('[MasterSync]', 'Flushing Tauri monitoring data...');
      const result = await tauri.invoke('flush_pending_monitoring_data');
      debugLog('[MasterSync]', 'Monitoring flush result:', result);
    } catch (error) {
      console.warn('[MasterSync] Monitoring flush failed (data retained locally):', error);
    }
  }

  /**
   * Trigger sync for a single module while reusing the unified progress and
   * completion lifecycle.
   */
  async triggerModuleSync(module: SyncModule, options?: {
    attendanceMode?: 'personal' | 'kiosk' | 'single' | 'auto';
  }): Promise<SyncResult | null> {
    if (!this.config) {
      console.error('[MasterSync] Not configured');
      return null;
    }

    if (this.isSyncing) {
      debugLog('[MasterSync]', 'Sync already in progress');
      return null;
    }

    if (this.isPreparingOffline) {
      debugLog('[MasterSync]', 'Offline preparation already in progress');
      return null;
    }

    if (!networkState.isOnline()) {
      debugLog('[MasterSync]', 'Offline - cannot sync');
      this.updateProgress({ stage: 'idle', message: 'No network connection' });
      return null;
    }

    if (!this.shouldSyncModule(module)) {
      debugLog('[MasterSync]', `Skipping ${module} - no access`);
      return {
        success: true,
        module,
        uploaded: 0,
        downloaded: 0,
        failed: 0,
        errors: [],
        durationMs: 0,
      };
    }

    this.isSyncing = true;
    this.syncAbortController = new AbortController();
    const startTime = Date.now();

    try {
      this.updateProgress({
        stage: 'checking',
        module,
        message: `Checking pending ${module} items...`,
        current: 0,
        total: 0,
      });

      const result = await this.syncModule(module, options);

      this.updateProgress({
        stage: 'complete',
        module,
        message: `${module} sync complete`,
        current: result.success ? 1 : 0,
        total: 1,
      });

      this.lastFullSyncAt = new Date();
      this.lastSyncResultsSnapshot = [result];
      this.persistSyncMetadata();

      this.config?.onComplete?.([result]);
      debugLog('[MasterSync]', `${module} sync completed in ${Date.now() - startTime}ms`);
      return result;
    } catch (error: any) {
      if (!isSilentFetchError(error)) console.error(`[MasterSync] ${module} sync failed:`, error);
      this.updateProgress({
        stage: 'error',
        module,
        message: error.message || `${module} sync failed`,
        error: error.message,
      });
      this.config?.onError?.(error);
      return {
        success: false,
        module,
        uploaded: 0,
        downloaded: 0,
        failed: 0,
        errors: [error.message || `${module} sync failed`],
        durationMs: Date.now() - startTime,
      };
    } finally {
      this.isSyncing = false;
      this.syncAbortController = null;

      setTimeout(() => {
        if (!this.isSyncing) {
          this.updateProgress({ stage: 'idle', message: '', current: 0, total: 0 });
        }
      }, 3000);
    }
  }

  async triggerOperationsSync(): Promise<SyncResult | null> {
    return this.triggerModuleSync('operations');
  }

  async triggerAttendanceSync(attendanceMode?: 'personal' | 'kiosk' | 'single' | 'auto'): Promise<SyncResult | null> {
    return this.triggerModuleSync('attendance', { attendanceMode });
  }

  async refreshAttendanceSnapshot(modeOverride?: 'personal' | 'kiosk' | 'single' | 'auto'): Promise<{
    downloadedEmployees: number;
    downloadedTemplates: number;
    downloadedShifts: number;
    downloadedGeofences: number;
    mode: 'personal' | 'kiosk' | 'single';
  }> {
    if (!this.config) {
      throw new Error('Not configured');
    }

    if (!networkState.isOnline()) {
      throw new Error('No network connection');
    }

    return this.downloadAttendanceSnapshot(modeOverride);
  }

  async syncAttendanceConfiguration(configType: AttendanceConfigSyncType): Promise<AttendanceConfigSyncResult> {
    const mode = (configType === 'employees' || configType === 'templates')
      ? 'kiosk'
      : 'personal';

    const snapshot = await this.refreshAttendanceSnapshot(mode);
    return {
      success: true,
      configType,
      mode: snapshot.mode,
      downloadedEmployees: snapshot.downloadedEmployees,
      downloadedTemplates: snapshot.downloadedTemplates,
      downloadedShifts: snapshot.downloadedShifts,
      downloadedGeofences: snapshot.downloadedGeofences,
    };
  }

  async prepareAttendanceOfflineAssets(
    options: PrepareAttendanceOfflineAssetsOptions,
  ): Promise<PrepareAttendanceOfflineAssetsResult> {
    const module = await import('./offline/prepareAttendanceOfflineAssets');
    return module.prepareAttendanceOfflineAssets(options);
  }

  async getCachedAttendanceSnapshot(params: {
    clientId?: number | null;
  } = {}): Promise<OfflineAttendanceData | null> {
    const clientId = this.resolveClientId(params.clientId);
    return loadUnifiedAttendanceSnapshot(clientId).catch(() => null);
  }

  async saveCachedAttendanceSnapshot(params: {
    clientId?: number | null;
    snapshot: OfflineAttendanceData;
  }): Promise<void> {
    const clientId = this.resolveClientId(params.clientId);
    await persistUnifiedAttendanceSnapshot(clientId, params.snapshot).catch((cacheError) => {
      console.warn('[MasterSync] Failed to persist unified attendance snapshot cache:', cacheError);
      throw cacheError;
    });
  }

  async getCachedAttendanceTemplateCount(params: {
    clientId?: number | null;
  } = {}): Promise<number> {
    const attendanceData = await this.getCachedAttendanceSnapshot(params);
    return attendanceData?.face_templates?.length || 0;
  }

  async getCachedAttendanceGeofences(params: {
    clientId?: number | null;
  } = {}): Promise<CachedAttendanceGeofence[]> {
    const clientId = this.resolveClientId(params.clientId);
    const cachedGeofences = await offlineStorage.getAllGeofences().catch(() => []);

    return cachedGeofences
      .filter((loc: any) => {
        if (loc?.client_id == null) return true;
        return Number(loc.client_id) === clientId;
      })
      .map((loc: any) => {
        const primaryGps = Array.isArray(loc.identifiers?.gps) ? loc.identifiers.gps[0] : null;
        const latitude = loc.center_latitude ?? primaryGps?.latitude;
        const longitude = loc.center_longitude ?? primaryGps?.longitude;
        const radius = loc.radius ?? primaryGps?.radius ?? 100;

        if (latitude == null || longitude == null) return null;

        return {
          id: loc.id,
          name: loc.name,
          latitude,
          longitude,
          radius_meters: radius,
          identifiers: loc.identifiers || [],
        } satisfies CachedAttendanceGeofence;
      })
      .filter(Boolean) as CachedAttendanceGeofence[];
  }

  async updateCachedAttendanceEmployeeStatus(params: {
    clientId?: number | null;
    employeeId: number;
    status: AttendanceQueueStatus;
  }): Promise<void> {
    const clientId = this.resolveClientId(params.clientId);
    await this.persistAttendanceStatus(clientId, params.employeeId, params.status);
  }

  async getCachedPersonalAttendanceStats(params: {
    clientId?: number | null;
  } = {}): Promise<AttendancePersonalStatsCache | null> {
    this.resolveClientId(params.clientId);
    return offlineStorage.getCachedPersonalStats()
      .then<AttendancePersonalStatsCache | null>((stats) => {
        if (!stats) {
          return null;
        }

        if (stats.status !== 'clocked_in' && stats.status !== 'clocked_out' && stats.status !== 'on_break') {
          return null;
        }

        const normalizedStatus: AttendancePersonalStatsCache['status'] = stats.status;

        return {
          ...stats,
          status: normalizedStatus,
        };
      })
      .catch(() => null);
  }

  async cachePersonalAttendanceStats(params: {
    clientId?: number | null;
    stats: AttendancePersonalStatsCache;
  }): Promise<void> {
    this.resolveClientId(params.clientId);
    await offlineStorage.cachePersonalStats(params.stats);
  }

  async getCachedCompanyAttendanceStats(params: {
    clientId?: number | null;
  } = {}): Promise<AttendanceCompanyStatsCache | null> {
    this.resolveClientId(params.clientId);
    return offlineStorage.getCachedCompanyStats().catch(() => null);
  }

  async cacheCompanyAttendanceStats(params: {
    clientId?: number | null;
    stats: AttendanceCompanyStatsCache;
  }): Promise<void> {
    this.resolveClientId(params.clientId);
    await offlineStorage.cacheCompanyStats(params.stats);
  }

  async queueAttendanceAction(params: {
    clientId?: number | null;
    log: PendingAttendanceLog;
  }): Promise<{ queuedLog: PendingAttendanceLog; newStatus: AttendanceQueueStatus }> {
    const clientId = this.resolveClientId(params.clientId ?? params.log.client_id);
    const normalizedAction = normalizeQueuedAttendanceAction(params.log.action_type || params.log.action);
    const queuedAction = buildOfflineClockActionFromPendingLog(clientId, params.log);

    const { queueId, newStatus } = await offlineStorage.queueClockActionWithStatusUpdate(
      queuedAction,
      normalizedAction,
    );

    await attendanceStore.setClockStatus(
      clientId,
      toFiniteNumber(params.log.employee_id),
      newStatus,
    ).catch((error) => {
      console.warn('[MasterSync] Failed to persist dedicated employee status key:', error);
    });

    const queuedLog = mapUnifiedPendingClockAction(
      {
        ...queuedAction,
        id: queueId,
      },
      params.log.id || queuedAction.offline_id,
    );

    return { queuedLog, newStatus };
  }

  async getPendingAttendanceCount(clientIdOverride?: number | null): Promise<number> {
    this.resolveClientId(clientIdOverride);
    return offlineStorage.getPendingActionsCount().catch(() => 0);
  }

  async cacheOperationsWorkItems(
    workItems: Array<OperationalFormWorkItemTarget & Record<string, any>>,
    clientIdOverride?: number | null,
    filterKey?: string | null,
  ): Promise<number> {
    const clientId = this.resolveClientId(clientIdOverride);
    if (!Array.isArray(workItems) || workItems.length === 0) {
      return 0;
    }

    const cacheKey = filterKey ? `${WORK_ITEM_FILTER_CACHE_PREFIX}${filterKey}` : WORK_ITEM_CACHE_KEY;
    await saveOperationsWorkItemsList(clientId, cacheKey, workItems as CachedOperationsWorkItem[]).catch((error) => {
      console.warn('[MasterSync] Failed to store work item list cache:', error);
    });

    if (!filterKey) {
      await bulkUpsertUnifiedWorkItemCache(
        clientId,
        workItems
          .map((item) => normalizeStoredWorkItemRecord(item))
          .filter((item): item is StoredOfflineWorkItem => item !== null),
      ).catch((error) => {
        console.warn('[MasterSync] Failed to cache work item per-item records in unified store:', error);
      });
    }

    return workItems.length;
  }

  async cacheFleetVehicles(
    vehicles: Array<Record<string, any>>,
    clientIdOverride?: number | null,
  ): Promise<number> {
    const clientId = this.resolveClientId(clientIdOverride);
    await appShellStore.saveSettingsArrayMigration(clientId, FLEET_VEHICLES_CACHE_KEY, Array.isArray(vehicles) ? vehicles : []);
    return Array.isArray(vehicles) ? vehicles.length : 0;
  }

  async getCachedFleetVehicles(params: {
    clientId?: number | null;
  } = {}): Promise<Array<Record<string, any>>> {
    const clientId = this.resolveClientId(params.clientId);
    return appShellStore.loadSettingsArrayMigration<Record<string, any>>(clientId, FLEET_VEHICLES_CACHE_KEY).catch(() => []);
  }

  // Per-(clientId,cacheKey) pending upsert buffer used to coalesce bursts of
  // realtime events into a single encrypted read+write cycle. Without this,
  // each WS event would trigger an extra read + write on the
  // native bridge, which can saturate the Capacitor plugin queue during cold
  // start when the WS replays a backlog of events.
  private pendingWorkItemUpserts = new Map<string, {
    items: Map<string, OperationalFormWorkItemTarget & Record<string, unknown>>;
    timer: ReturnType<typeof setTimeout> | null;
  }>();
  private static readonly WORK_ITEM_UPSERT_FLUSH_MS = 150;

  async upsertCachedOperationsWorkItem(params: {
    clientId?: number | null;
    workItem: OperationalFormWorkItemTarget & Record<string, any>;
    filterKey?: string | null;
  }): Promise<void> {
    const clientId = this.resolveClientId(params.clientId);
    const incomingItem = params.workItem;

    if (!incomingItem?.id) {
      return;
    }

    const cacheKey = params.filterKey ? `${WORK_ITEM_FILTER_CACHE_PREFIX}${params.filterKey}` : WORK_ITEM_CACHE_KEY;
    const bufferKey = `${clientId}::${cacheKey}`;

    let pending = this.pendingWorkItemUpserts.get(bufferKey);
    if (!pending) {
      pending = { items: new Map(), timer: null };
      this.pendingWorkItemUpserts.set(bufferKey, pending);
    }
    // Last-write-wins per id within the flush window.
    pending.items.set(String(incomingItem.id), incomingItem);

    if (pending.timer) {
      return;
    }

    pending.timer = setTimeout(() => {
      void this.flushPendingWorkItemUpserts(clientId, cacheKey, bufferKey);
    }, MasterSync.WORK_ITEM_UPSERT_FLUSH_MS);
  }

  private async flushPendingWorkItemUpserts(
    clientId: number,
    cacheKey: string,
    bufferKey: string,
  ): Promise<void> {
    const pending = this.pendingWorkItemUpserts.get(bufferKey);
    if (!pending) {
      return;
    }
    this.pendingWorkItemUpserts.delete(bufferKey);

    const incomingItems = Array.from(pending.items.values());
    if (incomingItems.length === 0) {
      return;
    }

    const existingList = await loadOperationsWorkItemsList<CachedOperationsWorkItem>(clientId, cacheKey).catch(() => []);
    const nextList = Array.isArray(existingList) ? [...existingList] : [];

    for (const incomingItem of incomingItems) {
      const existingIndex = nextList.findIndex((item) => String(item?.id) === String(incomingItem.id));
      if (incomingItem.deleted_at) {
        if (existingIndex >= 0) {
          nextList.splice(existingIndex, 1);
        }
      } else if (existingIndex >= 0) {
        nextList[existingIndex] = {
          ...(nextList[existingIndex] as Record<string, any>),
          ...incomingItem,
        };
      } else {
        nextList.unshift(incomingItem);
      }
    }

    await saveOperationsWorkItemsList(clientId, cacheKey, nextList).catch((error) => {
      console.warn('[MasterSync] Failed to upsert work item list cache:', cacheKey, error);
    });

    // Per-item cache: only keep the most recent payload per id, written
    // sequentially to avoid hammering the native bridge in parallel.
    for (const incomingItem of incomingItems) {
      try {
        const normalizedItem = normalizeStoredWorkItemRecord(incomingItem);
        if (!normalizedItem) {
          continue;
        }
        await upsertUnifiedWorkItemCache(clientId, normalizedItem);
      } catch (error) {
        console.warn('[MasterSync] Failed to upsert work item per-item cache:', error);
      }
    }
  }

  /**
   * Merge a partial work-item update into offline list + per-item caches
   * (used after realtime payloads so the next cold read matches UI).
   */
  async mergeCachedOperationsWorkItemPatch(params: {
    clientId?: number | null;
    workItemId: string;
    patch: Record<string, unknown>;
    filterKey?: string | null;
  }): Promise<void> {
    const clientId = this.resolveClientId(params.clientId);
    const workItemId = String(params.workItemId);
    if (!params.patch || Object.keys(params.patch).length === 0) {
      return;
    }

    const mergeIntoList = async (cacheKey: string) => {
      const list =
        (await loadOperationsWorkItemsList<Record<string, unknown>>(clientId, cacheKey).catch(() => [])) || [];
      if (!Array.isArray(list) || list.length === 0) {
        return;
      }
      const idx = list.findIndex((i) => i && typeof i === 'object' && String((i as { id?: unknown }).id) === workItemId);
      if (idx < 0) {
        return;
      }
      const row = list[idx] as Record<string, unknown>;
      const next = [...list];
      next[idx] = { ...row, ...params.patch, id: row.id };
      await saveOperationsWorkItemsList(clientId, cacheKey, next).catch((error) => {
        console.warn('[MasterSync] Failed to merge work item into list cache:', cacheKey, error);
      });
    };

    try {
      const cachedAll = await listUnifiedCachedWorkItems(clientId).catch(() => []);
      const existing = cachedAll.find((i) => String(i.id) === workItemId);
      if (existing) {
        const normalizedItem = normalizeStoredWorkItemRecord({ ...existing, ...params.patch, id: existing.id });
        if (normalizedItem) {
          await upsertUnifiedWorkItemCache(clientId, normalizedItem).catch((error) => {
            console.warn('[MasterSync] Failed to merge work item into per-item cache:', error);
          });
        }
      }

      await mergeIntoList(WORK_ITEM_CACHE_KEY);

      if (params.filterKey) {
        await mergeIntoList(`${WORK_ITEM_FILTER_CACHE_PREFIX}${params.filterKey}`);
      }
    } catch (error) {
      console.warn('[MasterSync] mergeCachedOperationsWorkItemPatch failed:', error);
    }
  }

  getPendingFleetInspections(params: {
    clientId?: number | null;
  } = {}): QueuedFleetInspection[] {
    const clientId = this.resolveClientId(params.clientId);
    const scopedKey = this.getFleetInspectionQueueStorageKey(clientId);
    const scopedQueue = this.readLocalStorageJson<QueuedFleetInspection[]>(scopedKey, []);

    if (scopedQueue.length > 0) {
      return scopedQueue;
    }

    return this.readLocalStorageJson<QueuedFleetInspection[]>(LEGACY_FLEET_INSPECTION_QUEUE_KEY, []);
  }

  savePendingFleetInspections(params: {
    clientId?: number | null;
    inspections: QueuedFleetInspection[];
  }): void {
    const clientId = this.resolveClientId(params.clientId);
    const scopedKey = this.getFleetInspectionQueueStorageKey(clientId);

    if (!Array.isArray(params.inspections) || params.inspections.length === 0) {
      this.removeLocalStorageKey(scopedKey);
      return;
    }

    this.writeLocalStorageJson(scopedKey, params.inspections);
  }

  queueFleetInspection(params: {
    clientId?: number | null;
    inspection: Omit<QueuedFleetInspection, 'id' | 'createdAt' | 'retryCount'>;
  }): QueuedFleetInspection {
    const existing = this.getPendingFleetInspections({ clientId: params.clientId });
    const queuedInspection: QueuedFleetInspection = {
      ...params.inspection,
      id: generateMasterSyncQueueId('fleet_inspection'),
      createdAt: new Date().toISOString(),
      retryCount: 0,
    };

    this.savePendingFleetInspections({
      clientId: params.clientId,
      inspections: [...existing, queuedInspection],
    });

    return queuedInspection;
  }

  getPendingFleetInspectionCount(params: {
    clientId?: number | null;
  } = {}): number {
    return this.getPendingFleetInspections(params).length;
  }

  async saveVehicleInspectionDraft(params: {
    vehicleId: string;
    draft: Omit<VehicleInspectionDraft, 'vehicleId' | 'savedAt'>;
  }): Promise<void> {
    const storageKey = this.getVehicleInspectionDraftStorageKey(params.vehicleId);
    const payload: VehicleInspectionDraft = {
      vehicleId: params.vehicleId,
      ...params.draft,
      savedAt: Date.now(),
    };

    try {
      const clientId = this.getAppShellClientId();
      if (!clientId) {
        throw new Error('Missing client context for vehicle inspection draft persistence');
      }

      await appShellStore.upsertJson(clientId, this.getVehicleInspectionDraftEntityId(params.vehicleId), payload);
      this.removeLocalStorageKey(storageKey);
    } catch (error) {
      console.warn('[MasterSync] Vehicle inspection draft save failed in offline DB, falling back to localStorage:', error);
      this.writeLocalStorageJson(storageKey, payload);
    }
  }

  async getVehicleInspectionDraft(params: {
    vehicleId: string;
    maxAgeMs?: number;
  }): Promise<VehicleInspectionDraft | null> {
    const storageKey = this.getVehicleInspectionDraftStorageKey(params.vehicleId);
    const clientId = this.getAppShellClientId();
    let draft: VehicleInspectionDraft | null = null;

    if (clientId) {
      try {
        draft = await appShellStore.getJsonUnifiedOnly<VehicleInspectionDraft>(clientId, this.getVehicleInspectionDraftEntityId(params.vehicleId));
      } catch (error) {
        console.warn('[MasterSync] Failed to read vehicle inspection draft from offline DB:', error);
      }
    }

    if (!draft) {
      draft = this.readLocalStorageJson<VehicleInspectionDraft | null>(storageKey, null);

      if (draft && clientId) {
        void appShellStore.upsertJson(clientId, this.getVehicleInspectionDraftEntityId(params.vehicleId), draft)
          .then(() => {
            this.removeLocalStorageKey(storageKey);
          })
          .catch((error) => {
            console.warn('[MasterSync] Failed to migrate vehicle inspection draft into offline DB:', error);
          });
      }
    }

    if (!draft) {
      return null;
    }

    const maxAgeMs = params.maxAgeMs ?? 4 * 60 * 60 * 1000;
    if (Date.now() - draft.savedAt > maxAgeMs) {
      await this.clearVehicleInspectionDraft({ vehicleId: params.vehicleId });
      return null;
    }

    return draft;
  }

  async clearVehicleInspectionDraft(params: {
    vehicleId: string;
  }): Promise<void> {
    const storageKey = this.getVehicleInspectionDraftStorageKey(params.vehicleId);
    const clientId = this.getAppShellClientId();

    if (clientId) {
      try {
        await appShellStore.deleteJson(clientId, this.getVehicleInspectionDraftEntityId(params.vehicleId));
      } catch (error) {
        console.warn('[MasterSync] Failed to clear vehicle inspection draft from offline DB:', error);
      }
    }

    this.removeLocalStorageKey(storageKey);
  }

  // Latest submitted inspection snapshot per vehicle — lets the inspection
  // modal prefill the checklist even when the device is offline (the live
  // getInspectionEntries fetch can't reach the API). Written whenever the
  // latest inspection is fetched or a new one is submitted. No expiry: a
  // stale checklist is still a better starting point than a blank one.
  async saveLastVehicleInspectionSnapshot(params: {
    vehicleId: string;
    snapshot: Omit<LastVehicleInspectionSnapshot, 'vehicleId' | 'savedAt'>;
  }): Promise<void> {
    if (!params.vehicleId) return;

    const storageKey = this.getLastVehicleInspectionSnapshotStorageKey(params.vehicleId);
    const payload: LastVehicleInspectionSnapshot = {
      vehicleId: params.vehicleId,
      ...params.snapshot,
      savedAt: Date.now(),
    };

    try {
      const clientId = this.getAppShellClientId();
      if (!clientId) {
        throw new Error('Missing client context for vehicle inspection snapshot persistence');
      }

      await appShellStore.upsertJson(clientId, this.getLastVehicleInspectionSnapshotEntityId(params.vehicleId), payload);
      this.removeLocalStorageKey(storageKey);
    } catch (error) {
      console.warn('[MasterSync] Vehicle inspection snapshot save failed in offline DB, falling back to localStorage:', error);
      this.writeLocalStorageJson(storageKey, payload);
    }
  }

  async getLastVehicleInspectionSnapshot(params: {
    vehicleId: string;
  }): Promise<LastVehicleInspectionSnapshot | null> {
    if (!params.vehicleId) return null;

    const storageKey = this.getLastVehicleInspectionSnapshotStorageKey(params.vehicleId);
    const clientId = this.getAppShellClientId();
    let snapshot: LastVehicleInspectionSnapshot | null = null;

    if (clientId) {
      try {
        snapshot = await appShellStore.getJsonUnifiedOnly<LastVehicleInspectionSnapshot>(clientId, this.getLastVehicleInspectionSnapshotEntityId(params.vehicleId));
      } catch (error) {
        console.warn('[MasterSync] Failed to read vehicle inspection snapshot from offline DB:', error);
      }
    }

    if (!snapshot) {
      snapshot = this.readLocalStorageJson<LastVehicleInspectionSnapshot | null>(storageKey, null);

      if (snapshot && clientId) {
        void appShellStore.upsertJson(clientId, this.getLastVehicleInspectionSnapshotEntityId(params.vehicleId), snapshot)
          .then(() => {
            this.removeLocalStorageKey(storageKey);
          })
          .catch((error) => {
            console.warn('[MasterSync] Failed to migrate vehicle inspection snapshot into offline DB:', error);
          });
      }
    }

    return snapshot;
  }

  async saveSmartTaskRoutePlan(params: {
    travelSessionId: string;
    routePlanId: string;
    workItemId?: string | null;
  }): Promise<PersistedSmartTaskRoutePlan> {
    const payload: PersistedSmartTaskRoutePlan = {
      travelSessionId: params.travelSessionId,
      routePlanId: params.routePlanId,
      workItemId: params.workItemId ?? null,
      savedAt: new Date().toISOString(),
    };

    const storageKey = this.getSmartTaskRoutePlanStorageKey(params.travelSessionId);

    try {
      const clientId = this.getAppShellClientId();
      if (!clientId) {
        throw new Error('Missing client context for SmartTask route plan persistence');
      }

      await appShellStore.upsertJson(clientId, this.getSmartTaskRoutePlanEntityId(params.travelSessionId), payload);
      this.removeLocalStorageKey(storageKey);
    } catch (error) {
      console.warn('[MasterSync] Failed to persist SmartTask route plan in offline DB, falling back to localStorage:', error);
      this.writeLocalStorageJson(storageKey, payload);
    }

    return payload;
  }

  async getSmartTaskRoutePlan(params: {
    travelSessionId: string;
  }): Promise<PersistedSmartTaskRoutePlan | null> {
    const storageKey = this.getSmartTaskRoutePlanStorageKey(params.travelSessionId);
    const clientId = this.getAppShellClientId();

    if (clientId) {
      try {
        const routePlan = await appShellStore.getJsonUnifiedOnly<PersistedSmartTaskRoutePlan>(clientId, this.getSmartTaskRoutePlanEntityId(params.travelSessionId));
        if (routePlan) {
          return routePlan;
        }
      } catch (error) {
        console.warn('[MasterSync] Failed to read SmartTask route plan from offline DB:', error);
      }
    }

    const legacyRoutePlan = this.readLocalStorageJson<PersistedSmartTaskRoutePlan | null>(storageKey, null);
    if (legacyRoutePlan && clientId) {
      void appShellStore.upsertJson(clientId, this.getSmartTaskRoutePlanEntityId(params.travelSessionId), legacyRoutePlan)
        .then(() => {
          this.removeLocalStorageKey(storageKey);
        })
        .catch((error) => {
          console.warn('[MasterSync] Failed to migrate SmartTask route plan into offline DB:', error);
        });
    }

    return legacyRoutePlan;
  }

  async clearSmartTaskRoutePlan(params: {
    travelSessionId: string;
  }): Promise<void> {
    const storageKey = this.getSmartTaskRoutePlanStorageKey(params.travelSessionId);
    const clientId = this.getAppShellClientId();

    if (clientId) {
      try {
        await appShellStore.deleteJson(clientId, this.getSmartTaskRoutePlanEntityId(params.travelSessionId));
      } catch (error) {
        console.warn('[MasterSync] Failed to clear SmartTask route plan from offline DB:', error);
      }
    }

    this.removeLocalStorageKey(storageKey);
  }

  async queueSmartTaskEquipmentRecord(params: {
    clientId?: number | null;
    record: SmartTaskEquipmentOfflineRecord;
    storageKey?: string;
  }): Promise<string> {
    const clientId = this.resolveClientId(params.clientId);
    const storageKey = params.storageKey
      || `equipment_${params.record.action}_${params.record.item_id}_${Date.now()}`;

    await saveQueuedEquipmentRecord(clientId, storageKey, {
      item_id: params.record.item_id,
      action: params.record.action,
      payload: params.record.payload as Record<string, unknown>,
      photos: params.record.photos,
    });

    return storageKey;
  }

  async getCachedOperationsWorkItems(params: {
    clientId?: number | null;
    filterKey?: string | null;
  } = {}): Promise<CachedOperationsWorkItem[]> {
    const startedAt = this.getSmartTaskColdStartNow();
    const clientId = this.resolveClientId(params.clientId);
    this.writeSmartTaskColdStartLog('MASTERSYNC_CACHE_WORK_ITEMS_START', 'Loading cached Smart Task operations work items', {
      clientId,
    });

    const cacheKey = params.filterKey ? `${WORK_ITEM_FILTER_CACHE_PREFIX}${params.filterKey}` : WORK_ITEM_CACHE_KEY;
    const [listCache, perItemCache] = await Promise.all([
      loadOperationsWorkItemsList<CachedOperationsWorkItem>(clientId, cacheKey).catch(() => []),
      params.filterKey ? Promise.resolve([]) : listUnifiedCachedWorkItems(clientId).catch(() => []),
    ]);

    const combined = [
      ...(Array.isArray(listCache) ? listCache : []),
      ...(Array.isArray(perItemCache) ? perItemCache : []),
    ].filter((item): item is CachedOperationsWorkItem => Boolean(item?.id));

    if (combined.length === 0) {
      this.writeSmartTaskColdStartDuration('MASTERSYNC_CACHE_WORK_ITEMS_EMPTY', 'No cached Smart Task operations work items were found', startedAt, {
        clientId,
      });
      return [];
    }

    const deduped = new Map<string, CachedOperationsWorkItem>();
    for (const item of combined) {
      deduped.set(String(item.id), item);
    }

    const resolvedItems = Array.from(deduped.values()).filter((item) => !item.deleted_at);
    this.writeSmartTaskColdStartDuration('MASTERSYNC_CACHE_WORK_ITEMS_READY', 'Loaded cached Smart Task operations work items', startedAt, {
      clientId,
      listCacheCount: Array.isArray(listCache) ? listCache.length : 0,
      perItemCacheCount: Array.isArray(perItemCache) ? perItemCache.length : 0,
      resolvedCount: resolvedItems.length,
    });
    return resolvedItems;
  }

  async queueSmartTaskPhoto(params: {
    clientId?: number | null;
    photo: QueueSmartTaskPhotoInput;
  }): Promise<OfflineWorkItemPhoto> {
    const startedAt = this.getSmartTaskColdStartNow();
    await this.ensureOperationsClientContext(params.clientId);
    this.writeSmartTaskColdStartLog('MASTERSYNC_QUEUE_PHOTO_START', 'Queueing Smart Task photo in MasterSync', {
      workItemId: params.photo.work_item_id,
      photoType: params.photo.photo_type,
      fileSize: params.photo.file_size,
    });
    const savedPhoto = await offlineWorkItems.savePhotoOffline(params.photo);
    this.writeSmartTaskColdStartDuration('MASTERSYNC_QUEUE_PHOTO_READY', 'Queued Smart Task photo in MasterSync', startedAt, {
      photoId: savedPhoto.id,
      workItemId: savedPhoto.work_item_id,
    });
    return savedPhoto;
  }

  async queueSmartTaskSignature(params: {
    clientId?: number | null;
    signature: QueueSmartTaskSignatureInput;
  }): Promise<OfflineWorkItemSignature> {
    await this.ensureOperationsClientContext(params.clientId);
    return offlineWorkItems.saveSignatureOffline(params.signature);
  }

  async prepareOperationsOfflineStorage(params: {
    clientId?: number | null;
  } = {}): Promise<void> {
    const startedAt = this.getSmartTaskColdStartNow();
    this.writeSmartTaskColdStartLog('MASTERSYNC_PREPARE_OPERATIONS_STORAGE_START', 'Preparing MasterSync operations offline storage', {
      clientId: params.clientId || undefined,
    });
    const clientId = await this.ensureOperationsClientContext(params.clientId);
    this.writeSmartTaskColdStartDuration('MASTERSYNC_PREPARE_OPERATIONS_STORAGE_READY', 'Prepared MasterSync operations offline storage', startedAt, {
      clientId,
    });
  }

  /**
   * Sync the current user's workflow permission group memberships from
   * /api/v1/iam/my-workflow-memberships into the offline store. This is the
   * sole source of truth for which workflow tiles appear on BeginActionModal.
   * Safe to call without sync config — only needs an auth token.
   */
  async syncWorkflowGroupMemberships(): Promise<{ success: boolean; workflowKeys: number }> {
    const startedAt = this.getSmartTaskColdStartNow();
    const token = authService.getTokens()?.access_token;
    if (!token) {
      return { success: false, workflowKeys: 0 };
    }

    const clientId = this.resolveClientId(null);
    if (!clientId) {
      return { success: false, workflowKeys: 0 };
    }

    try {
      const res = await fetch(buildApiUrl('/api/v1/iam/my-workflow-memberships'), {
        headers: { 'Authorization': `Bearer ${token}` },
        cache: 'no-store',
      });

      if (!res.ok) {
        this.writeSmartTaskColdStartLog(
          'MASTERSYNC_WORKFLOW_MEMBERSHIPS_FAIL',
          `Workflow memberships fetch returned ${res.status}`,
          { clientId },
        );
        return { success: false, workflowKeys: 0 };
      }

      const data = await res.json();
      if (!data?.success) {
        return { success: false, workflowKeys: 0 };
      }

      const workflowKeys: string[] = Array.isArray(data.workflow_keys) ? data.workflow_keys : [];
      const memberships = Array.isArray(data.memberships) ? data.memberships : [];

      await workflowGroupMembershipStore.save(clientId, {
        workflow_keys: workflowKeys,
        memberships,
      });

      this.writeSmartTaskColdStartDuration(
        'MASTERSYNC_WORKFLOW_MEMBERSHIPS_READY',
        'Synced workflow group memberships',
        startedAt,
        { clientId, workflowKeys: workflowKeys.length },
      );

      return { success: true, workflowKeys: workflowKeys.length };
    } catch (error: any) {
      if (!isSilentFetchError(error)) {
        debugLog('[MasterSync]', 'Workflow memberships sync failed:', error?.message || error);
      }
      return { success: false, workflowKeys: 0 };
    }
  }

  /**
   * Sync the current user's effective permissions from
   * /api/v1/iam/effective-permissions into the offline store. This is the
   * offline source of truth for action permissions (create, resolve, close,
   * smart_task, etc.) and feature settings that control what a user can do
   * inside workflow modals. Safe to call without sync config — only needs
   * an auth token.
   */
  async syncEffectivePermissions(moduleKey = 'total-operations'): Promise<{ success: boolean }> {
    const startedAt = this.getSmartTaskColdStartNow();
    const token = authService.getTokens()?.access_token;
    if (!token) {
      return { success: false };
    }

    const clientId = this.resolveClientId(null);
    if (!clientId) {
      return { success: false };
    }

    try {
      const res = await fetch(buildApiUrl(`/api/v1/iam/effective-permissions?module_key=${encodeURIComponent(moduleKey)}`), {
        headers: { 'Authorization': `Bearer ${token}` },
        cache: 'no-store',
      });

      if (!res.ok) {
        this.writeSmartTaskColdStartLog(
          'MASTERSYNC_EFFECTIVE_PERMISSIONS_FAIL',
          `Effective permissions fetch returned ${res.status}`,
          { clientId, moduleKey },
        );
        return { success: false };
      }

      const data = await res.json();
      if (!data?.success) {
        return { success: false };
      }

      await effectivePermissionsStore.save(clientId, moduleKey, {
        module_key: moduleKey,
        permissions: data.permissions || {},
        role: data.role || 'user',
        has_access: data.has_access ?? false,
        resolved_via: data.resolved_via || {},
        overrides_only: data.overrides_only || {},
        is_field_worker: data.is_field_worker ?? false,
        is_root: data.is_root ?? false,
      });

      this.writeSmartTaskColdStartDuration(
        'MASTERSYNC_EFFECTIVE_PERMISSIONS_READY',
        'Synced effective permissions',
        startedAt,
        { clientId, moduleKey },
      );

      return { success: true };
    } catch (error: any) {
      if (!isSilentFetchError(error)) {
        debugLog('[MasterSync]', 'Effective permissions sync failed:', error?.message || error);
      }
      return { success: false };
    }
  }

  async getOperationsStorageStats(params: {
    clientId?: number | null;
  } = {}): Promise<{
    workItems: number;
    photos: number;
    signatures: number;
    checklists: number;
    timeEntries: number;
    documents: number;
    pendingOperations: number;
    pendingUploads: number;
    storageUsedMB: number;
  }> {
    const startedAt = this.getSmartTaskColdStartNow();
    await this.ensureOperationsClientContext(params.clientId);
    const stats = await offlineWorkItems.getStorageStats();
    this.writeSmartTaskColdStartDuration('MASTERSYNC_STORAGE_STATS_READY', 'Loaded MasterSync operations storage stats', startedAt, {
      clientId: params.clientId || undefined,
      workItems: stats.workItems,
      photos: stats.photos,
      signatures: stats.signatures,
      pendingUploads: stats.pendingUploads,
      storageUsedMB: stats.storageUsedMB,
    });
    return stats;
  }

  async getPendingSmartTaskPhotos(params: {
    clientId?: number | null;
    workItemId?: string | null;
  } = {}): Promise<OfflineWorkItemPhoto[]> {
    const startedAt = this.getSmartTaskColdStartNow();
    await this.ensureOperationsClientContext(params.clientId);
    const photos = params.workItemId
      ? await offlineWorkItems.getPendingPhotosForWorkItem(String(params.workItemId))
      : await offlineWorkItems.getPendingPhotos();

    this.writeSmartTaskColdStartDuration('MASTERSYNC_PENDING_PHOTOS_READY', 'Loaded pending Smart Task photos from MasterSync', startedAt, {
      clientId: params.clientId || undefined,
      workItemId: params.workItemId || undefined,
      photoCount: photos.length,
    });
    return photos;
  }

  async getPendingSmartTaskSignatures(params: {
    clientId?: number | null;
    workItemId?: string | null;
  } = {}): Promise<OfflineWorkItemSignature[]> {
    await this.ensureOperationsClientContext(params.clientId);
    const signatures = await offlineWorkItems.getPendingSignatures();

    if (!params.workItemId) {
      return signatures;
    }

    return signatures.filter((signature) => String(signature.work_item_id) === String(params.workItemId));
  }

  async deletePendingSmartTaskPhoto(params: {
    clientId?: number | null;
    photoId: string;
  }): Promise<void> {
    await this.ensureOperationsClientContext(params.clientId);
    await offlineWorkItems.deletePhotoOffline(params.photoId);
  }

  async deletePendingSmartTaskSignature(params: {
    clientId?: number | null;
    signatureId: string;
  }): Promise<void> {
    await this.ensureOperationsClientContext(params.clientId);
    await offlineWorkItems.deleteSignatureOffline(params.signatureId);
  }

  async queueSmartTaskChildEntry(params: {
    clientId?: number | null;
    payload: PendingChildEntryPayload;
  }): Promise<PendingChildEntry> {
    const clientId = this.resolveClientId(params.clientId);
    const queue = await loadPendingChildEntries(clientId);
    const entry: PendingChildEntry = {
      id: generateMasterSyncQueueId('child_entry'),
      payload: params.payload,
      createdAt: new Date().toISOString(),
      retryCount: 0,
    };

    queue.push(entry);
    await savePendingChildEntries(clientId, queue);
    return entry;
  }

  async queueSmartTaskQuoteRequest(params: {
    clientId?: number | null;
    payload: PendingQuoteRequestPayload;
    relationshipPayload?: {
      source_work_item_id: string;
      target_work_item_id: string;
      relationship_type: string;
    };
  }): Promise<PendingQuoteRequest> {
    const clientId = this.resolveClientId(params.clientId);
    const queue = await loadPendingQuoteRequests(clientId);
    const entry: PendingQuoteRequest = {
      id: generateMasterSyncQueueId('quote_request'),
      payload: params.payload,
      relationshipPayload: params.relationshipPayload,
      createdAt: new Date().toISOString(),
      retryCount: 0,
    };

    queue.push(entry);
    await savePendingQuoteRequests(clientId, queue);
    return entry;
  }

  async queueSmartTaskBomItem(params: {
    clientId?: number | null;
    workItemId: string;
    payload: PendingBomItemPayload;
  }): Promise<PendingBomItem> {
    const clientId = this.resolveClientId(params.clientId);
    const queue = await loadPendingBomItems(clientId);
    const entry: PendingBomItem = {
      id: generateMasterSyncQueueId('bom_item'),
      workItemId: params.workItemId,
      payload: params.payload,
      createdAt: new Date().toISOString(),
      retryCount: 0,
    };

    queue.push(entry);
    await savePendingBomItems(clientId, queue);
    return entry;
  }

  async queueSmartTaskWorkflowTransition(params: {
    clientId?: number | null;
    userId?: number | null;
    solutionKey?: string | null;
    action: Parameters<typeof queueSmartTaskOfflineTransition>[0]['action'];
    expectedFromState: Parameters<typeof queueSmartTaskOfflineTransition>[0]['expectedFromState'];
    toState: Parameters<typeof queueSmartTaskOfflineTransition>[0]['toState'];
    workItemId?: string | null;
    payload: Parameters<typeof queueSmartTaskOfflineTransition>[0]['payload'];
    timelineLabel?: string;
    activityContext?: SmartTaskOfflineActivityContext | null;
  }) {
    return queueSmartTaskOfflineTransition({
      ...params,
      clientId: this.resolveClientId(params.clientId),
      userId: this.resolveUserId(params.userId),
    });
  }

  async queueSmartTaskWorkflowReset(params: {
    clientId?: number | null;
    userId?: number | null;
    solutionKey?: string | null;
    workItemId?: string | null;
    payload: Parameters<typeof queueSmartTaskOfflineWorkflowReset>[0]['payload'];
    timelineLabel?: string;
    activityContext?: SmartTaskOfflineActivityContext | null;
  }) {
    return queueSmartTaskOfflineWorkflowReset({
      ...params,
      clientId: this.resolveClientId(params.clientId),
      userId: this.resolveUserId(params.userId),
    });
  }

  async queueSmartTaskStatusUpdate(params: {
    clientId?: number | null;
    userId?: number | null;
    solutionKey?: string | null;
    workItemId: string;
    status: string;
    payload: Parameters<typeof queueSmartTaskOfflineStatusUpdate>[0]['payload'];
    timelineLabel?: string;
    activityContext?: SmartTaskOfflineActivityContext | null;
  }) {
    return queueSmartTaskOfflineStatusUpdate({
      ...params,
      clientId: this.resolveClientId(params.clientId),
      userId: this.resolveUserId(params.userId),
    });
  }

  async queueSmartTaskVehicleAction(params: {
    clientId?: number | null;
    userId?: number | null;
    solutionKey?: string | null;
    action: Parameters<typeof queueSmartTaskOfflineVehicleAction>[0]['action'];
    workItemId?: string | null;
    vehicleId?: string | null;
    vehicleRegistration?: string | null;
    payload: Parameters<typeof queueSmartTaskOfflineVehicleAction>[0]['payload'];
    timelineLabel?: string;
    activityContext?: SmartTaskOfflineActivityContext | null;
  }) {
    return queueSmartTaskOfflineVehicleAction({
      ...params,
      clientId: this.resolveClientId(params.clientId),
      userId: this.resolveUserId(params.userId),
    });
  }

  async queueSmartTaskFleetAction(params: {
    clientId?: number | null;
    userId?: number | null;
    solutionKey?: string | null;
    action: SmartTaskFleetAction;
    workItemId?: string | null;
    vehicleId?: string | null;
    vehicleRegistration?: string | null;
    payload: Parameters<typeof queueSmartTaskOfflineFleetAction>[0]['payload'];
    timelineLabel?: string;
    activityContext?: SmartTaskOfflineActivityContext | null;
  }) {
    return queueSmartTaskOfflineFleetAction({
      ...params,
      clientId: this.resolveClientId(params.clientId),
      userId: this.resolveUserId(params.userId),
    });
  }

  async queueSmartTaskFieldServiceAction(params: {
    clientId?: number | null;
    userId?: number | null;
    solutionKey?: string | null;
    action: SmartTaskFieldServiceAction;
    workItemId: string;
    payload: Parameters<typeof queueSmartTaskOfflineFieldServiceAction>[0]['payload'];
    timelineLabel?: string;
    activityContext?: SmartTaskOfflineActivityContext | null;
  }) {
    return queueSmartTaskOfflineFieldServiceAction({
      ...params,
      clientId: this.resolveClientId(params.clientId),
      userId: this.resolveUserId(params.userId),
    });
  }

  async queueSmartTaskLogbookAction(params: {
    clientId?: number | null;
    userId?: number | null;
    solutionKey?: string | null;
    action: SmartTaskLogbookAction;
    workItemId?: string | null;
    payload: Parameters<typeof queueSmartTaskOfflineLogbookAction>[0]['payload'];
    timelineLabel?: string;
    activityContext?: SmartTaskOfflineActivityContext | null;
  }) {
    return queueSmartTaskOfflineLogbookAction({
      ...params,
      clientId: this.resolveClientId(params.clientId),
      userId: this.resolveUserId(params.userId),
    });
  }

  async queueSmartTaskComment(params: {
    clientId?: number | null;
    userId?: number | null;
    solutionKey?: string | null;
    workItemId: string;
    payload: {
      comment_text: string;
      is_internal?: boolean;
      comment_type?: WorkItemCommentType;
      is_itemized?: boolean;
      mentioned_user_ids?: number[];
      parent_comment_id?: string | null;
      activity_metadata?: Record<string, any>;
    };
    timelineLabel?: string;
    activityContext?: SmartTaskOfflineActivityContext | null;
  }) {
    return queueSmartTaskOfflineCommentCreate({
      ...params,
      clientId: this.resolveClientId(params.clientId),
      userId: this.resolveUserId(params.userId),
    });
  }

  async getPendingSmartTaskVehicleActionCount(params: {
    clientId?: number | null;
    userId?: number | null;
    action?: SmartTaskVehicleAction;
  } = {}): Promise<number> {
    const queue = await this.getPendingSmartTaskActions(params);

    return queue.filter((entry) => (
      entry.kind === 'vehicle_action'
      && (!params.action || entry.action === params.action)
    )).length;
  }

  async getPendingSmartTaskActions(params: {
    clientId?: number | null;
    userId?: number | null;
  } = {}): Promise<SmartTaskQueuedAction[]> {
    return loadPendingSmartTaskActions(
      this.resolveClientId(params.clientId),
      this.resolveUserId(params.userId),
    );
  }

  async getPendingSmartTaskComments(params: {
    clientId?: number | null;
    userId?: number | null;
    workItemIds?: string[];
  } = {}): Promise<SmartTaskQueuedCommentCreate[]> {
    return loadPendingSmartTaskComments(
      this.resolveClientId(params.clientId),
      this.resolveUserId(params.userId),
      params.workItemIds,
    );
  }

  async getPendingSmartTaskActionCount(params: {
    clientId?: number | null;
    userId?: number | null;
  } = {}): Promise<number> {
    return (await this.getPendingSmartTaskActions(params)).length;
  }

  async getSmartTaskOfflineActionHistory(params: {
    clientId?: number | null;
    userId?: number | null;
    limit?: number;
  } = {}): Promise<SmartTaskOfflineActionHistoryEntry[]> {
    return loadSmartTaskOfflineActionHistory(
      this.resolveClientId(params.clientId),
      this.resolveUserId(params.userId),
      params.limit ?? 10,
    );
  }

  async getCachedSmartTaskWorkflowState(params: {
    clientId?: number | null;
    userId?: number | null;
  } = {}): Promise<WorkflowStateRecord | null> {
    return loadCachedSmartTaskWorkflowState(
      this.resolveClientId(params.clientId),
      this.resolveUserId(params.userId),
    );
  }

  async saveCachedSmartTaskWorkflowState(params: {
    clientId?: number | null;
    userId?: number | null;
    state: WorkflowStateRecord;
  }): Promise<void> {
    await saveCachedSmartTaskWorkflowState({
      clientId: this.resolveClientId(params.clientId),
      userId: this.resolveUserId(params.userId),
      state: params.state,
    });
  }

  async syncPendingSmartTaskActionsNow(params: {
    clientId?: number | null;
    userId?: number | null;
    token: string;
    onProgress?: (progress: { current: number; total: number; message: string }) => void;
  }): Promise<{ synced: number; failed: number; remaining: number }> {
    const result = await syncPendingSmartTaskActions({
      clientId: this.resolveClientId(params.clientId),
      userId: this.resolveUserId(params.userId),
      token: params.token,
      onProgress: params.onProgress,
    });

    emitOperationsSyncPendingChanged('smarttask');
    return result;
  }

  syncPendingSmartTaskActionsInBackground(params: {
    clientId?: number | null;
    userId?: number | null;
    token: string;
    onProgress?: (progress: { current: number; total: number; message: string }) => void;
    onComplete?: (result: { synced: number; failed: number; remaining: number }) => void;
    onError?: (error: unknown) => void;
  }): void {
    if (this.isBgSmartTaskSyncRunning) {
      debugLog('[MasterSync]', 'Background SmartTask sync already in progress, skipping duplicate call');
      return;
    }

    this.isBgSmartTaskSyncRunning = true;
    const startedAt = this.getSmartTaskColdStartNow();
    this.writeSmartTaskColdStartLog('MASTERSYNC_BG_SYNC_START', 'Starting MasterSync background Smart Task action sync', {
      clientId: params.clientId || undefined,
      userId: params.userId || undefined,
    });
    Promise.resolve()
      .then(async () => {
        const result = await this.syncPendingSmartTaskActionsNow({
          clientId: params.clientId,
          userId: params.userId,
          token: params.token,
          onProgress: params.onProgress,
        });
        this.writeSmartTaskColdStartDuration('MASTERSYNC_BG_SYNC_READY', 'Completed MasterSync background Smart Task action sync', startedAt, {
          synced: result.synced,
          failed: result.failed,
          remaining: result.remaining,
        });
        params.onComplete?.(result);
      })
      .catch((error) => {
        if (!isSilentFetchError(error)) console.warn('[MasterSync] Background SmartTask action sync failed:', error);
        this.writeSmartTaskColdStartDuration('MASTERSYNC_BG_SYNC_ERROR', 'MasterSync background Smart Task action sync failed', startedAt, {
          error: error instanceof Error ? error.message : String(error),
        });
        params.onError?.(error);
      })
      .finally(() => {
        this.isBgSmartTaskSyncRunning = false;
      });
  }

  async queueSmartTaskRouteStopUpdate(params: {
    clientId?: number | null;
    userId?: number | null;
    solutionKey?: string | null;
    routeId: string;
    workItemId?: string | null;
    stopWorkItemId: string;
    stopStatus: Parameters<typeof queueSmartTaskOfflineRouteStopUpdate>[0]['stopStatus'];
    payload: Parameters<typeof queueSmartTaskOfflineRouteStopUpdate>[0]['payload'];
    timelineLabel?: string;
    activityContext?: SmartTaskOfflineActivityContext | null;
  }) {
    return queueSmartTaskOfflineRouteStopUpdate({
      ...params,
      clientId: this.resolveClientId(params.clientId),
      userId: this.resolveUserId(params.userId),
    });
  }

  /**
   * Cancel ongoing sync
   */
  cancelSync(): void {
    if (this.syncAbortController) {
      this.syncAbortController.abort();
      this.isSyncing = false;
      this.updateProgress({ stage: 'idle', message: 'Sync cancelled' });
    }
  }

  // ==================== MODULE SYNC ====================

  private shouldSyncModule(module: SyncModule): boolean {
    if (!this.config) return false;

    switch (module) {
      case 'attendance':
        return this.config.moduleAccess.attendance;
      case 'operations':
        return this.config.moduleAccess.operations;
      case 'connect':
        return this.config.moduleAccess.connect;
      default:
        return false;
    }
  }

  private async syncModule(module: SyncModule, options?: {
    attendanceMode?: 'personal' | 'kiosk' | 'single' | 'auto';
  }): Promise<SyncResult> {
    const startTime = Date.now();
    let uploaded = 0;
    let downloaded = 0;
    let failed = 0;
    const errors: string[] = [];
    let conflictsDetected = 0;
    let conflictsResolved = 0;
    let conflictsManual = 0;

    try {
      switch (module) {
        case 'attendance':
          const attendanceResult = await this.syncAttendance(options?.attendanceMode);
          uploaded = attendanceResult.uploaded;
          downloaded = attendanceResult.downloaded;
          failed = attendanceResult.failed;
          errors.push(...attendanceResult.errors);
          break;

        case 'operations':
          const workItemResult = await this.syncWorkItems();
          uploaded = workItemResult.uploaded;
          downloaded = workItemResult.downloaded;
          failed = workItemResult.failed;
          errors.push(...workItemResult.errors);
          break;

        case 'connect':
          const messageResult = await this.syncMessages();
          const emailResult = await this.syncEmails();
          uploaded = messageResult.uploaded + emailResult.uploaded;
          downloaded = messageResult.downloaded + emailResult.downloaded;
          failed = messageResult.failed + emailResult.failed;
          errors.push(...messageResult.errors, ...emailResult.errors);
          break;
      }

      // Process any pending conflicts collected during sync
      if (this.pendingConflicts.length > 0) {
        const moduleConflicts = this.pendingConflicts.filter(
          c => c.module === (module as SyncModuleName),
        );
        conflictsDetected = moduleConflicts.length;

        if (conflictsDetected > 0) {
          const { resolved, manual } = processConflicts(moduleConflicts);
          conflictsResolved = resolved.length;
          conflictsManual = manual.length;

          // Remove processed conflicts from pending list
          this.pendingConflicts = this.pendingConflicts.filter(
            c => c.module !== (module as SyncModuleName),
          );

          if (manual.length > 0) {
            debugLog('[MasterSync]', `${manual.length} conflict(s) require manual resolution for ${module}`);
          }
        }
      }

      return {
        success: errors.length === 0,
        module,
        uploaded,
        downloaded,
        failed,
        errors,
        durationMs: Date.now() - startTime,
        conflictsDetected,
        conflictsResolved,
        conflictsManual,
      };

    } catch (error: any) {
      return {
        success: false,
        module,
        uploaded,
        downloaded,
        failed,
        errors: [error.message],
        durationMs: Date.now() - startTime,
        conflictsDetected,
        conflictsResolved,
        conflictsManual,
      };
    }
  }

  // ==================== ATTENDANCE SYNC ====================

  async syncAttendance(attendanceMode?: 'personal' | 'kiosk' | 'single' | 'auto'): Promise<{ uploaded: number; downloaded: number; failed: number; errors: string[] }> {
    if (!this.config) throw new Error('Not configured');

    let uploaded = 0;
    let downloaded = 0;
    let failed = 0;
    const errors: string[] = [];

    const pendingBeforeSync = await offlineStorage.getPendingActionsCount().catch(() => 0);

    this.updateProgress({
      stage: 'uploading-attendance',
      module: 'attendance',
      message: 'Uploading attendance logs...',
      current: 0,
      total: pendingBeforeSync,
    });

    try {
      const syncResult = await offlineStorage.syncPendingActions();
      uploaded = syncResult.synced + syncResult.duplicates;
      failed = syncResult.failed;
      errors.push(...syncResult.errors);

      this.updateProgress({ current: pendingBeforeSync, total: pendingBeforeSync });
    } catch (err: any) {
      if (err.name === 'AbortError') throw err;
      errors.push(`Upload: ${err.message}`);
    }

    // Download fresh attendance data (employees, templates, shifts, geofences)
    this.updateProgress({
      stage: 'downloading-attendance',
      module: 'attendance',
      message: 'Downloading attendance data...',
      current: 0,
      total: 1
    });

    try {
      const attendanceDownload = await this.downloadAttendanceSnapshot(attendanceMode);
      downloaded = attendanceDownload.downloadedEmployees + attendanceDownload.downloadedTemplates;

      this.updateProgress({ current: 1 });

    } catch (err: any) {
      if (err.name === 'AbortError') throw err;
      errors.push(`Download: ${err.message}`);
    }

    // Generate draft timesheets after successful attendance sync
    if (errors.length === 0 && this.config?.clientId) {
      try {
        const { generateDraftTimesheetsForPeriod } = await import('@/lib/timesheet/clientSideTimesheetGenerator');
        const currentPeriod = 'current-month';
        const draftTimesheets = await generateDraftTimesheetsForPeriod(this.config.clientId, currentPeriod);

        if (draftTimesheets.length > 0) {
          debugLog('[MasterSync]', `Generated ${draftTimesheets.length} draft timesheets after attendance sync`);
        }
      } catch (timesheetError) {
        // Don't fail the sync if timesheet generation fails
        debugLog('[MasterSync]', 'Failed to generate draft timesheets', timesheetError);
      }
    }

    return { uploaded, downloaded, failed, errors };
  }

  async prepareOffline(options: MasterSyncPrepareOfflineOptions = {}): Promise<MasterSyncPrepareOfflineResult> {
    const startedAt = this.getSmartTaskColdStartNow();
    this.writeSmartTaskColdStartLog('MASTERSYNC_PREPARE_OFFLINE_START', 'Starting MasterSync offline preparation', {
      hasAttendanceMode: Boolean(options.attendanceMode),
      providedWorkItems: Array.isArray(options.workItems) ? options.workItems.length : 0,
    });
    if (!this.config) {
      return {
        success: false,
        attendance: null,
        operations: null,
        errors: ['Not configured'],
      };
    }

    if (this.isSyncing) {
      return {
        success: false,
        attendance: null,
        operations: null,
        errors: ['Sync already in progress'],
      };
    }

    if (this.isPreparingOffline) {
      return {
        success: false,
        attendance: null,
        operations: null,
        errors: ['Offline preparation already in progress'],
      };
    }

    if (!networkState.isOnline()) {
      return {
        success: false,
        attendance: null,
        operations: null,
        errors: ['No network connection'],
      };
    }

    this.isPreparingOffline = true;
    this.offlinePreparationAbortController = new AbortController();

    const errors: string[] = [];
    let attendance: MasterSyncPrepareOfflineResult['attendance'] = null;
    let operations: MasterSyncPrepareOfflineResult['operations'] = null;

    try {
      if (options.attendanceMode) {
        try {
          attendance = await this.downloadAttendanceSnapshot(
            options.attendanceMode,
            this.getOfflinePreparationSignal(),
          );
        } catch (error: any) {
          errors.push(error.message || 'Failed to prepare attendance offline data');
        }
      }

      let workItems = Array.isArray(options.workItems)
        ? options.workItems.filter((item): item is OperationalFormWorkItemTarget & Record<string, any> => Boolean(item?.id))
        : [];
      let cachedVehicles = 0;

      const shouldFetchAssignedWorkItems = workItems.length === 0 && this.config.moduleAccess.operations;

      if (shouldFetchAssignedWorkItems) {
        try {
          workItems = await this.fetchAssignedWorkItemsForOffline(this.getOfflinePreparationSignal());
        } catch (error: any) {
          errors.push(error.message || 'Failed to load assigned work items');
        }
      }

      if (workItems.length > 0 || shouldFetchAssignedWorkItems) {
        let cachedWorkItems = 0;
        let formsResult = { success: true, cached: 0, failed: 0, cachedFormKeys: [] as string[] };

        try {
          cachedWorkItems = workItems.length > 0
            ? await this.cacheWorkItemsForOffline(workItems)
            : 0;
        } catch (error: any) {
          errors.push(error.message || 'Failed to cache assigned work items');
        }

        if (this.config.moduleAccess.operations) {
          try {
            cachedVehicles = (await this.fetchFleetVehiclesForOffline(this.getOfflinePreparationSignal())).length;
          } catch (error: any) {
            errors.push(error.message || 'Failed to load fleet vehicles');
          }
        }

        try {
          formsResult = workItems.length > 0
            ? await this.syncOperationalFormTemplates(workItems, { reportProgress: false })
            : { success: true, cached: 0, failed: 0, cachedFormKeys: [] };
        } catch (error: any) {
          errors.push(error.message || 'Failed to cache operational forms');
        }

        operations = {
          cachedWorkItems,
          cachedForms: formsResult.cached,
          cachedVehicles,
          failedForms: formsResult.failed,
        };
      }

      // Sync workflow group memberships (BeginActionModal tile visibility)
      await this.syncWorkflowGroupMemberships();
      await this.syncEffectivePermissions();

      return {
        success: errors.length === 0,
        attendance,
        operations,
        errors,
      };
    } finally {
      this.writeSmartTaskColdStartDuration('MASTERSYNC_PREPARE_OFFLINE_READY', 'Completed MasterSync offline preparation', startedAt, {
        success: errors.length === 0,
        errorCount: errors.length,
        cachedWorkItems: operations?.cachedWorkItems || 0,
        cachedForms: operations?.cachedForms || 0,
        cachedVehicles: operations?.cachedVehicles || 0,
        failedForms: operations?.failedForms || 0,
      });
      this.isPreparingOffline = false;
      this.offlinePreparationAbortController = null;
    }
  }

  // ==================== MESSAGES SYNC ====================

  private async syncMessages(): Promise<{ uploaded: number; downloaded: number; failed: number; errors: string[] }> {
    if (!this.config) throw new Error('Not configured');

    const { clientId, authToken } = this.config;
    let uploaded = 0;
    const downloaded = 0;
    let failed = 0;
    const errors: string[] = [];

    this.updateProgress({
      stage: 'uploading-messages',
      module: 'connect',
      message: 'Uploading messages...'
    });

    try {
      const pendingMessages = await offlineMessages.getPendingMessagesForMasterSync(clientId);

      if (pendingMessages.length > 0) {
        this.updateProgress({ current: 0, total: pendingMessages.length });

        for (let i = 0; i < pendingMessages.length; i++) {
          const msg = pendingMessages[i];

          try {
            const response = await fetch(buildApiUrl(`/api/v1/messaging/threads/${msg.thread_id}/messages`), {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${authToken}`
              },
              body: JSON.stringify({
                // The messaging API rejects empty content — attachment-only
                // messages use the '📎 Attachment' placeholder (also applied
                // at queue time, this covers entries queued before that fix)
                content: msg.content?.trim() ? msg.content : (msg.attachments?.length ? '📎 Attachment' : msg.content),
                content_type: msg.content_type,
                attachments: msg.attachments,
                mentions: msg.mentions,
                offline_id: msg.offline_id,
              }),
              signal: this.getSyncSignal()
            });

            if (response.ok) {
              let serverMessageId: string | undefined;
              try {
                const body = await response.json();
                if (body && typeof body === 'object' && typeof (body as { id?: string }).id === 'string') {
                  serverMessageId = (body as { id: string }).id;
                }
              } catch {
                /* ignore parse — mark sent without server id */
              }
              await offlineMessages.markQueuedMessageSent(clientId, msg.offline_id, serverMessageId);
              uploaded++;
            } else {
              const error = await response.json();
              errors.push(`Message ${msg.offline_id}: ${error.error || 'Upload failed'}`);
              failed++;
            }

            this.updateProgress({ current: i + 1 });

          } catch (err: unknown) {
            if (err instanceof Error && err.name === 'AbortError') throw err;
            errors.push(`Message ${msg.offline_id}: ${err instanceof Error ? err.message : String(err)}`);
            failed++;
          }
        }
      }
    } catch (err: unknown) {
      if (err instanceof Error && err.name === 'AbortError') throw err;
      errors.push(`Message upload: ${err instanceof Error ? err.message : String(err)}`);
    }

    return { uploaded, downloaded, failed, errors };
  }

  // ==================== EMAIL SYNC ====================

  private async syncEmails(): Promise<{ uploaded: number; downloaded: number; failed: number; errors: string[] }> {
    if (!this.config) throw new Error('Not configured');

    const { clientId, authToken } = this.config;
    let uploaded = 0;
    const downloaded = 0;
    let failed = 0;
    const errors: string[] = [];

    this.updateProgress({
      stage: 'uploading-emails',
      module: 'connect',
      message: 'Sending queued emails...'
    });

    try {
      const queuedEmails = await offlineEmail.getPendingEmailsForMasterSync(clientId);

      if (queuedEmails.length > 0) {
        this.updateProgress({ current: 0, total: queuedEmails.length });

        for (let i = 0; i < queuedEmails.length; i++) {
          const email = queuedEmails[i];

          try {
            const response = await fetch(buildApiUrl('/api/v1/email/send'), {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${authToken}`
              },
              body: JSON.stringify({
                mailbox_email: email.mailbox_email,
                to: email.to,
                cc: email.cc,
                bcc: email.bcc,
                subject: email.subject,
                html_body: email.html_body,
                text_body: email.text_body,
                attachments: email.attachments,
                in_reply_to: email.in_reply_to_id,
                thread_id: email.thread_id,
                offline_id: email.offline_id,
              }),
              signal: this.getSyncSignal()
            });

            if (response.ok) {
              let serverMessageId: string | undefined;
              try {
                const body = await response.json();
                if (body && typeof body === 'object' && typeof (body as { messageId?: string }).messageId === 'string') {
                  serverMessageId = (body as { messageId: string }).messageId;
                }
              } catch {
                /* optional */
              }
              await offlineEmail.markQueuedEmailSent(clientId, email.offline_id, serverMessageId);
              uploaded++;
            } else {
              const error = await response.json();
              errors.push(`Email ${email.offline_id}: ${error.error || 'Send failed'}`);
              await offlineEmail.markQueuedEmailFailed(clientId, email.offline_id, String(error.error || 'Send failed'));
              failed++;
            }

            this.updateProgress({ current: i + 1 });

          } catch (err: unknown) {
            if (err instanceof Error && err.name === 'AbortError') throw err;
            errors.push(`Email ${email.offline_id}: ${err instanceof Error ? err.message : String(err)}`);
            failed++;
          }
        }
      }
    } catch (err: unknown) {
      if (err instanceof Error && err.name === 'AbortError') throw err;
      errors.push(`Email send: ${err instanceof Error ? err.message : String(err)}`);
    }

    return { uploaded, downloaded, failed, errors };
  }

  // ==================== WORK ITEMS SYNC ====================

  private async runOperationsSyncStep(
    step: Pick<OperationSyncStepResult, 'key' | 'label'>,
    task: () => Promise<{ uploaded: number; failed: number; errors?: string[] }>,
  ): Promise<OperationSyncStepResult> {
    const startedAt = Date.now();

    try {
      const result = await task();
      const errors = Array.isArray(result.errors) ? result.errors : [];

      return {
        key: step.key,
        label: step.label,
        success: errors.length === 0 && (result.failed || 0) === 0,
        uploaded: result.uploaded || 0,
        failed: result.failed || 0,
        errors,
        durationMs: Date.now() - startedAt,
      };
    } catch (err: any) {
      if (err?.name === 'AbortError') {
        throw err;
      }

      return {
        key: step.key,
        label: step.label,
        success: false,
        uploaded: 0,
        failed: 1,
        errors: [err?.message || `${step.label} failed`],
        durationMs: Date.now() - startedAt,
      };
    }
  }

  private async syncWorkItems(): Promise<{ uploaded: number; downloaded: number; failed: number; errors: string[] }> {
    if (!this.config) throw new Error('Not configured');

    const { clientId, authToken, userId } = this.config;
    let uploaded = 0;
    const downloaded = 0;
    let failed = 0;
    const errors: string[] = [];
    const stepResults: OperationSyncStepResult[] = [];

    this.updateProgress({
      stage: 'uploading-workitems',
      module: 'operations',
      message: 'Syncing work items...'
    });

    const allowedWorkItemSyncFields = new Set([
      'item_type',
      'item_subtype',
      'module_context',
      'title',
      'description',
      'status',
      'priority',
      'severity',
      'assigned_to',
      'assigned_team',
      'assigned_users',
      'start_date',
      'due_date',
      'scheduled_start',
      'scheduled_end',
      'estimated_hours',
      'actual_hours',
      'estimated_cost',
      'actual_cost',
      'hourly_rate',
      'billable',
      'billed',
      'location_id',
      'location_name',
      'location_address',
      'location_coordinates',
      'contact_id',
      'contact_name',
      'contact_email',
      'contact_phone',
      'contact_person_id',
      'contact_person',
      'reporter_id',
      'reporter_name',
      'reporter_email',
      'reporter_phone',
      'sla_response_time',
      'sla_resolution_time',
      'progress_percentage',
      'tags',
      'labels',
      'custom_fields',
      'workspace_id',
      'parent_id',
      'project_id',
    ]);

    const pickAllowedWorkItemFields = (...sources: Array<Record<string, any> | null | undefined>) => {
      const payload: Record<string, any> = {};

      for (const source of sources) {
        if (!source || typeof source !== 'object') {
          continue;
        }

        for (const key of allowedWorkItemSyncFields) {
          if (source[key] !== undefined) {
            payload[key] = source[key];
          }
        }
      }

      return payload;
    };

    const getChangedFieldPayload = (item: { changes?: Record<string, { old: any; new: any }> }) => {
      const changedFields: Record<string, any> = {};

      for (const [field, change] of Object.entries(item.changes || {})) {
        if (allowedWorkItemSyncFields.has(field)) {
          changedFields[field] = change?.new;
        }
      }

      return changedFields;
    };

    const workItemsStep = await this.runOperationsSyncStep(
      { key: 'work-items', label: 'Work item changes' },
      async () => {
        let stepUploaded = 0;
        let stepFailed = 0;
        const stepErrors: string[] = [];

        await offlineWorkItems.handleClientSwitch(clientId).catch(() => null);

        const modifiedItems = await listUnifiedModifiedWorkItems(clientId);

        if (modifiedItems.length > 0) {
          this.updateProgress({ current: 0, total: modifiedItems.length });

          for (let i = 0; i < modifiedItems.length; i++) {
            const item = modifiedItems[i];

            try {
              const originalItemId = String(item.id);
              const basePayload = pickAllowedWorkItemFields(item.data, item as Record<string, any>);
              const changedFields = getChangedFieldPayload(item);
              const isPendingCreate = item.sync_status === 'pending_create';
              const payload: Record<string, any> = isPendingCreate
                ? {
                  ...basePayload,
                  ...changedFields,
                  item_type: changedFields.item_type ?? basePayload.item_type ?? item.item_type,
                  title: changedFields.title ?? basePayload.title ?? item.title,
                  status: changedFields.status ?? basePayload.status ?? item.status,
                }
                : {
                  ...(Object.keys(changedFields).length > 0 ? changedFields : basePayload),
                  item_type: changedFields.item_type ?? basePayload.item_type ?? item.item_type,
                };

              const endpoint = isPendingCreate
                ? buildApiUrl('/api/v1/work-items')
                : buildApiUrl(`/api/v1/work-items/${encodeURIComponent(String(item.id))}`);
              const method = isPendingCreate ? 'POST' : 'PUT';

              const response = await fetch(endpoint, {
                method,
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${authToken}`
                },
                body: JSON.stringify(payload),
                signal: this.getSyncSignal()
              });

              if (response.ok) {
                const result = await response.json().catch(() => ({}));
                const resolvedServerItem = result?.data || result?.item || null;
                const mergedSyncItem = {
                  ...item,
                  ...(resolvedServerItem && typeof resolvedServerItem === 'object' ? resolvedServerItem : {}),
                  item_type: resolvedServerItem?.item_type ?? payload.item_type ?? item.item_type,
                  title: resolvedServerItem?.title ?? payload.title ?? item.title,
                  status: resolvedServerItem?.status ?? payload.status ?? item.status,
                  data: pickAllowedWorkItemFields(
                    item.data,
                    item as Record<string, any>,
                    payload,
                    resolvedServerItem && typeof resolvedServerItem === 'object'
                      ? resolvedServerItem as Record<string, any>
                      : undefined,
                  ),
                  sync_status: 'cached' as const,
                  changes: undefined,
                };

                const resolvedItemId = String(mergedSyncItem.id);

                if (resolvedItemId !== originalItemId) {
                  await workItemsStore.deleteRecord('work_items', clientId, originalItemId).catch((deleteError) => {
                    console.warn('[MasterSync] Failed to remove stale local work item cache record:', deleteError);
                  });
                }

                const normalizedMergedItem = normalizeStoredWorkItemRecord(mergedSyncItem);
                if (normalizedMergedItem) {
                  await upsertUnifiedWorkItemCache(clientId, normalizedMergedItem);
                }
                stepUploaded++;
              } else {
                const error = await response.json().catch(() => null);
                stepErrors.push(`Work item ${item.id}: ${error?.error || error?.message || `Sync failed (${response.status})`}`);
                stepFailed++;
              }

              this.updateProgress({ current: i + 1 });
            } catch (err: any) {
              if (err.name === 'AbortError') throw err;
              stepErrors.push(`Work item ${item.id}: ${err.message}`);
              stepFailed++;
            }
          }
        }

        return { uploaded: stepUploaded, failed: stepFailed, errors: stepErrors };
      },
    );
    stepResults.push(workItemsStep);
    uploaded += workItemsStep.uploaded;
    failed += workItemsStep.failed;
    errors.push(...workItemsStep.errors);

    const uploadsStep = await this.runOperationsSyncStep(
      { key: 'uploads', label: 'SmartTask uploads' },
      async () => {
        this.updateProgress({
          stage: 'uploading-workitems',
          module: 'operations',
          message: 'Syncing SmartTask uploads...',
        });

        const uploadResult = await offlineWorkItems.processSyncQueue();
        return {
          uploaded: uploadResult.success || 0,
          failed: uploadResult.failed || 0,
          errors: [],
        };
      },
    );
    stepResults.push(uploadsStep);
    uploaded += uploadsStep.uploaded;
    failed += uploadsStep.failed;
    errors.push(...uploadsStep.errors);

    this.updateProgress({
      stage: 'uploading-workitems',
      module: 'operations',
      message: 'Syncing SmartTask queues in parallel...',
    });

    const parallelStepPromises: Array<Promise<OperationSyncStepResult>> = [
      this.runOperationsSyncStep(
        { key: 'child-entries', label: 'SmartTask child entries' },
        async () => {
          const childEntryResult = await syncPendingChildEntries({
            clientId,
            token: authToken,
          });

          return {
            uploaded: childEntryResult.synced || 0,
            failed: childEntryResult.failed || 0,
            errors: [],
          };
        },
      ),
      this.runOperationsSyncStep(
        { key: 'quote-requests', label: 'SmartTask quote requests' },
        async () => {
          const quoteRequestResult = await syncPendingQuoteRequests({
            clientId,
            token: authToken,
          });

          return {
            uploaded: quoteRequestResult.synced || 0,
            failed: quoteRequestResult.failed || 0,
            errors: [],
          };
        },
      ),
      this.runOperationsSyncStep(
        { key: 'bom-items', label: 'SmartTask BOM items' },
        async () => {
          const bomItemResult = await syncPendingBomItems({
            clientId,
            token: authToken,
          });

          return {
            uploaded: bomItemResult.synced || 0,
            failed: bomItemResult.failed || 0,
            errors: [],
          };
        },
      ),
      this.runOperationsSyncStep(
        { key: 'forms', label: 'Offline forms' },
        async () => {
          const formResult = await syncOfflineFormQueueNow({
            accessToken: authToken,
          });

          return {
            uploaded: formResult.synced || 0,
            failed: formResult.failed || 0,
            errors: [],
          };
        },
      ),
      this.runOperationsSyncStep(
        { key: 'inventory-movements', label: 'Inventory movements' },
        async () => {
          const movementResult = await syncPendingInventoryMovements({
            clientId,
            token: authToken,
          });

          return {
            uploaded: movementResult.synced || 0,
            failed: movementResult.failed || 0,
            errors: [],
          };
        },
      ),
      this.runOperationsSyncStep(
        { key: 'stock-counts', label: 'Stock count actions' },
        async () => {
          const stockCountResult = await syncPendingStockCountActions({
            clientId,
            token: authToken,
          });

          return {
            uploaded: stockCountResult.synced || 0,
            failed: stockCountResult.failed || 0,
            errors: [],
          };
        },
      ),
    ];

    if (Number.isFinite(userId) && userId > 0) {
      parallelStepPromises.push(
        this.runOperationsSyncStep(
          { key: 'workflow-actions', label: 'SmartTask workflow actions' },
          async () => {
            const workflowActionResult = await syncPendingSmartTaskActions({
              clientId,
              userId,
              token: authToken,
            });

            return {
              uploaded: workflowActionResult.synced || 0,
              failed: workflowActionResult.failed || 0,
              errors: [],
            };
          },
        ),
      );
    }

    const parallelStepResults = await Promise.all(parallelStepPromises);
    for (const stepResult of parallelStepResults) {
      stepResults.push(stepResult);
      uploaded += stepResult.uploaded;
      failed += stepResult.failed;
      errors.push(...stepResult.errors);
    }

    this.lastOperationsSyncStepResults = stepResults;
    this.persistSyncMetadata();

    // Sync workflow group memberships (BeginActionModal tile visibility source of truth)
    await this.syncWorkflowGroupMemberships();
    // Sync effective permissions (action permissions for workflow modals)
    await this.syncEffectivePermissions();

    if (typeof window !== 'undefined') {
      const syncedAt = new Date().toISOString();
      window.dispatchEvent(new CustomEvent('smarttask-sync-complete', {
        detail: {
          syncedAt,
          uploads: uploaded,
        },
      }));
      window.dispatchEvent(new CustomEvent('work-item-sync-complete', {
        detail: {
          success: uploaded,
          failed,
        },
      }));
    }

    return { uploaded, downloaded, failed, errors };
  }

  // ==================== STORAGE CLEANUP ====================

  private shouldRunCleanup(): boolean {
    const lastCleanup = this.readLocalStorageJson<string | null>(LAST_CLEANUP_AT_STORAGE_KEY, null);
    if (!lastCleanup) return true;
    return Date.now() - new Date(lastCleanup).getTime() > CLEANUP_INTERVAL_MS;
  }

  private recordCleanupRun(): void {
    this.writeLocalStorageJson(LAST_CLEANUP_AT_STORAGE_KEY, new Date().toISOString());
  }

  async runStorageCleanup(): Promise<{
    photosRemoved: number;
    signaturesRemoved: number;
    queueEntriesRemoved: number;
    bytesFreed: number;
  }> {
    const startedAt = this.getSmartTaskColdStartNow();
    this.writeSmartTaskColdStartLog('MASTERSYNC_CLEANUP_START', 'Starting storage cleanup', {});

    this.updateProgress({
      stage: 'cleanup-pruning',
      module: 'operations',
      message: 'Cleaning up old uploaded media...',
      current: 0,
      total: 1,
    });

    const result = await offlineWorkItems.pruneUploadedMedia(CLEANUP_MAX_AGE_DAYS);

    this.updateProgress({
      stage: 'cleanup-pruning',
      module: 'operations',
      message: `Cleaned up ${result.photosRemoved + result.signaturesRemoved} old items`,
      current: 1,
      total: 1,
    });

    this.writeSmartTaskColdStartDuration('MASTERSYNC_CLEANUP_COMPLETE', 'Storage cleanup complete', startedAt, result);
    this.recordCleanupRun();

    return result;
  }

  // ==================== COLD-START CACHE VALIDATION ====================

  /**
   * Proactive cold-start cache validation — runs early in the app lifecycle to
   * detect and clean stale cache data that can cause the dashboard to hang on
   * loading. This addresses the scenario where:
   *
   * 1. The app is updated and old cache data has incompatible schema/structure
   * 2. Auth tokens were rotated while the app was backgrounded, and cached
   *    data references stale credentials
   * 3. Navigation data in localStorage is older than COLD_START_CACHE_MAX_AGE_MS
   * 4. The offline DB has records with sync_status='pending' that are stuck
   *    (never completed sync) and blocking the loading flow
   *
   * Unlike runStorageCleanup (which only prunes old media), this method:
   * - Validates cache age and clears stale navigation/shell snapshots
   * - Removes stuck pending sync records that are older than the max age
   * - Clears the publish retry queue of dead-letter entries
   * - Records the check timestamp to avoid running on every cold start
   *
   * This is safe to call before the sync config is set — it only cleans data
   * that is clearly stale or stuck, not data that might still be valid.
   */
  async runColdStartCacheValidation(): Promise<{
    ranCleanup: boolean;
    clearedStaleNavigation: boolean;
    clearedStuckPending: boolean;
    clearedDeadLetterQueue: boolean;
    details: string[];
  }> {
    const details: string[] = [];
    let clearedStaleNavigation = false;
    let clearedStuckPending = false;
    let clearedDeadLetterQueue = false;

    if (typeof window === 'undefined') {
      return { ranCleanup: false, clearedStaleNavigation, clearedStuckPending, clearedDeadLetterQueue, details };
    }

    const now = Date.now();
    const lastCheck = this.readLocalStorageJson<string | null>(COLD_START_CACHE_CHECK_KEY, null);
    // Only run once per hour to avoid overhead on every cold start
    if (lastCheck && now - new Date(lastCheck).getTime() < 60 * 60 * 1000) {
      return { ranCleanup: false, clearedStaleNavigation, clearedStuckPending, clearedDeadLetterQueue, details };
    }

    this.writeLocalStorageJson(COLD_START_CACHE_CHECK_KEY, new Date().toISOString());

    // 1. Check for stale navigation data in localStorage
    try {
      const navDataRaw = window.localStorage.getItem(STALE_NAVIGATION_DATA_KEY);
      if (navDataRaw) {
        const navData = JSON.parse(navDataRaw);
        const cachedAt = navData?.cached_at || navData?.timestamp || navData?.updated_at;
        if (cachedAt) {
          const ageMs = now - new Date(cachedAt).getTime();
          if (ageMs > COLD_START_CACHE_MAX_AGE_MS) {
            window.localStorage.removeItem(STALE_NAVIGATION_DATA_KEY);
            clearedStaleNavigation = true;
            details.push(`Cleared stale navigation data (age: ${Math.round(ageMs / (60 * 60 * 1000))}h)`);
            console.warn('[MasterSync] Cold-start: cleared stale navigation data', { ageHours: Math.round(ageMs / (60 * 60 * 1000)) });
          }
        }
      }
    } catch (error) {
      // If we can't parse the navigation data, it's likely corrupt — clear it
      try {
        window.localStorage.removeItem(STALE_NAVIGATION_DATA_KEY);
        clearedStaleNavigation = true;
        details.push('Cleared unparseable navigation data');
      } catch {
        // ignore
      }
    }

    // 2. Check for stuck pending sync records in the offline DB
    if (offlineDb) {
      try {
        await offlineDb.open();
        const cutoff = new Date(now - COLD_START_CACHE_MAX_AGE_MS).toISOString();

        // Find pending records older than the max age — these are stuck
        const stuckPending = await offlineDb.records
          .where('updated_at')
          .below(cutoff)
          .and((record) => record.sync_status === 'pending' || record.sync_status === 'failed')
          .toArray();

        if (stuckPending.length > 0) {
          // Only clear if there are no outbox entries for the same entities
          // (outbox entries mean the user has unsent mutations we should preserve)
          const stuckIds = stuckPending.map((r) => r.id).filter((id): id is number => id != null);
          for (const id of stuckIds) {
            await offlineDb.records.delete(id).catch(() => {});
          }
          clearedStuckPending = true;
          details.push(`Cleared ${stuckPending.length} stuck pending/failed records (older than ${CLEANUP_MAX_AGE_DAYS}d)`);
          console.warn('[MasterSync] Cold-start: cleared stuck pending records', { count: stuckPending.length });
        }
      } catch (error) {
        if (!isSilentFetchError(error)) console.warn('[MasterSync] Cold-start: failed to check stuck pending records:', error);
      }

      // 3. Clean up dead-letter publish queue entries
      try {
        const removed = await this.cleanupPublishRetryQueue(CLEANUP_MAX_AGE_DAYS);
        if (removed > 0) {
          clearedDeadLetterQueue = true;
          details.push(`Cleared ${removed} dead-letter/completed publish queue entries`);
        }
      } catch (error) {
        if (!isSilentFetchError(error)) console.warn('[MasterSync] Cold-start: publish queue cleanup failed:', error);
      }
    }

    // 4. Also clear stale dashboard cached stats
    try {
      const cachedStatsRaw = window.localStorage.getItem('dashboard_cached_stats');
      if (cachedStatsRaw) {
        const cachedStats = JSON.parse(cachedStatsRaw);
        const cachedAt = cachedStats?.cachedAt;
        if (cachedAt) {
          const ageMs = now - new Date(cachedAt).getTime();
          if (ageMs > COLD_START_CACHE_MAX_AGE_MS) {
            window.localStorage.removeItem('dashboard_cached_stats');
            details.push('Cleared stale dashboard cached stats');
          }
        }
      }
    } catch {
      // ignore
    }

    const ranCleanup = clearedStaleNavigation || clearedStuckPending || clearedDeadLetterQueue;
    if (ranCleanup) {
      this.writeSmartTaskColdStartLog('MASTERSYNC_COLD_START_CLEANUP', 'Cold-start cache validation cleaned stale data', {
        clearedStaleNavigation,
        clearedStuckPending,
        clearedDeadLetterQueue,
        details,
      });
    }

    return { ranCleanup, clearedStaleNavigation, clearedStuckPending, clearedDeadLetterQueue, details };
  }

  // ==================== STATS ====================

  /**
   * Get current pending sync stats
   */
  async getStats(): Promise<MasterSyncStats> {
    if (!this.config) {
      return {
        pendingAttendanceLogs: 0,
        pendingMessages: 0,
        pendingEmails: 0,
        pendingWorkItems: 0,
        pendingWorkItemUploads: 0,
        pendingChildEntries: 0,
        pendingFormSubmissions: 0,
        pendingSmartTaskActions: 0,
        pendingInventoryMovements: 0,
        pendingStockCountActions: 0,
        pendingMutationLedger: 0,
        failedMutationLedger: 0,
        conflictMutationLedger: 0,
        pendingAttendanceMutations: 0,
        pendingOperationsMutations: 0,
        pendingConnectMutations: 0,
        oldestPendingMutationAgeMs: null,
        lastFullSync: this.lastFullSyncAt,
        lastSyncResults: this.lastSyncResultsSnapshot,
        moduleAccess: { attendance: false, operations: false, connect: false }
      };
    }

    const { clientId } = this.config;

    const [pendingAttendanceLogs, messages, emails, workItems, offlineWorkItemStats, pendingChildEntries, pendingQuoteRequests, pendingBomItems, pendingFormSubmissions, pendingSmartTaskActions, pendingInventoryMovements, pendingStockCountActions, mutationLedgerSummary] = await Promise.all([
      offlineStorage.getPendingActionsCount().catch(() => 0),
      offlineMessages.getPendingMessagesForMasterSync(clientId).catch(() => []),
      offlineEmail.getPendingEmailsForMasterSync(clientId).catch(() => []),
      listUnifiedModifiedWorkItems(clientId).catch(() => []),
      offlineWorkItems.getStorageStats().catch(() => ({ pendingOperations: 0, pendingUploads: 0 })),
      getPendingChildEntryCount(clientId).catch(() => 0),
      getPendingQuoteRequestCount(clientId).catch(() => 0),
      getPendingBomItemCount(clientId).catch(() => 0),
      getOfflineFormQueuePendingCount().catch(() => 0),
      Number.isFinite(this.config.userId) && this.config.userId > 0
        ? this.getPendingSmartTaskActionCount({ clientId, userId: this.config.userId }).catch(() => 0)
        : Promise.resolve(0),
      getPendingInventoryMovementCount(clientId).catch(() => 0),
      getPendingStockCountActionCount(clientId).catch(() => 0),
      getOfflineMutationLedgerSummary(clientId).catch(() => ({
        pending: 0,
        failed: 0,
        conflict: 0,
        oldestPendingAgeMs: null,
        byModule: { attendance: 0, operations: 0, connect: 0 },
      })),
    ]);

    const pendingOperations = [
      workItems.length,
      offlineWorkItemStats.pendingOperations || 0,
      pendingChildEntries,
      pendingQuoteRequests,
      pendingBomItems,
      pendingFormSubmissions,
      pendingSmartTaskActions,
      pendingInventoryMovements,
      pendingStockCountActions,
    ].reduce((sum, count) => sum + count, 0);

    return {
      pendingAttendanceLogs,
      pendingMessages: messages.length,
      pendingEmails: emails.length,
      pendingWorkItems: pendingOperations,
      pendingWorkItemUploads: offlineWorkItemStats.pendingUploads || 0,
      pendingChildEntries,
      pendingFormSubmissions,
      pendingSmartTaskActions,
      pendingInventoryMovements,
      pendingStockCountActions,
      pendingMutationLedger: mutationLedgerSummary.pending,
      failedMutationLedger: mutationLedgerSummary.failed,
      conflictMutationLedger: mutationLedgerSummary.conflict,
      pendingAttendanceMutations: mutationLedgerSummary.byModule.attendance,
      pendingOperationsMutations: mutationLedgerSummary.byModule.operations,
      pendingConnectMutations: mutationLedgerSummary.byModule.connect,
      oldestPendingMutationAgeMs: mutationLedgerSummary.oldestPendingAgeMs,
      lastFullSync: this.lastFullSyncAt,
      lastSyncResults: this.lastSyncResultsSnapshot,
      moduleAccess: this.config.moduleAccess
    };
  }

  getLastOperationsSyncStepResults(): OperationSyncStepResult[] {
    return [...this.lastOperationsSyncStepResults];
  }

  /**
   * Get total pending count
   */
  async getTotalPendingCount(): Promise<number> {
    const stats = await this.getStats();
    return getTotalPendingCountFromMasterStats(stats);
  }

  async cacheOperationalFormConfig(formKey: string, config: any): Promise<void> {
    await cacheOfflineFormConfig(formKey, config);
  }

  async getCachedOperationalFormConfig(formKey: string): Promise<any | null> {
    return getCachedOfflineFormConfig(formKey);
  }

  async queueOperationalFormSubmission(
    submission: Omit<QueuedSubmission, 'id' | 'queuedAt' | 'retries'>,
  ): Promise<void> {
    await queueOfflineFormSubmission(submission);
    emitOperationsSyncPendingChanged('forms');
  }

  async saveOperationalFormDraft(
    draft: Omit<StoredFormDraft, 'savedAt'>,
  ): Promise<StoredFormDraft> {
    return saveOfflineFormDraft(draft);
  }

  async getOperationalFormDraft(draftId: string): Promise<StoredFormDraft | null> {
    return getOfflineFormDraft(draftId);
  }

  async clearOperationalFormDraft(draftId: string): Promise<void> {
    await clearOfflineFormDraft(draftId);
  }

  async saveGenericFormDraft(
    draft: GenericFormDraftRecord,
  ): Promise<StoredFormDraft> {
    return saveOfflineFormDraft({
      id: draft.id,
      formKey: draft.formKey,
      formConfigId: draft.formConfigId,
      entityType: draft.entityType,
      entityId: draft.entityId,
      formData: draft.formData,
    });
  }

  async getGenericFormDraft(draftId: string): Promise<StoredFormDraft | null> {
    return getOfflineFormDraft(draftId);
  }

  async clearGenericFormDraft(draftId: string): Promise<void> {
    await clearOfflineFormDraft(draftId);
  }

  async getPendingOperationalFormCount(): Promise<number> {
    return getOfflineFormQueuePendingCount();
  }

  isOnline(): boolean {
    return networkState.isOnline();
  }

  /**
   * Predownload operational form templates for the supplied work items and cache
   * them for offline rendering/submission.
   */
  async syncOperationalFormTemplates(
    workItems: OperationalFormWorkItemTarget[],
    options: { reportProgress?: boolean } = {},
  ): Promise<OperationalFormTemplateSyncResult> {
    const startedAt = this.getSmartTaskColdStartNow();
    if (!this.config?.authToken) {
      return { success: false, cached: 0, failed: 0, cachedFormKeys: [] };
    }

    const normalizedTargets = Array.isArray(workItems)
      ? workItems
        .filter((item): item is OperationalFormWorkItemTarget => Boolean(item?.id))
        .map((item) => ({
          id: item.id,
          item_type: item.item_type || 'job',
          item_subtype: item.item_subtype || null,
          parent_id: item.parent_id || null,
          metadata: item.metadata || null,
          custom_fields: item.custom_fields || null,
        }))
      : [];

    if (normalizedTargets.length === 0) {
      return { success: true, cached: 0, failed: 0, cachedFormKeys: [] };
    }

    const uniqueTargets = Array.from(
      new Map(normalizedTargets.map((item) => [String(item.id), item])).values()
    );

    const shouldReportProgress = options.reportProgress !== false;
    this.writeSmartTaskColdStartLog('MASTERSYNC_FORM_TEMPLATES_START', 'Starting MasterSync operational form template sync', {
      workItemCount: uniqueTargets.length,
      reportProgress: shouldReportProgress,
    });

    try {
      if (shouldReportProgress) {
        this.updateProgress({
          stage: 'downloading-messages',
          module: 'operations',
          current: 0,
          total: uniqueTargets.length,
          message: 'Downloading operational form templates...',
        });
      }

      const result = await predownloadOperationalFormConfigs({
        accessToken: this.config.authToken,
        workItems: uniqueTargets,
        onProgress: ({ current, total, message }) => {
          if (shouldReportProgress) {
            this.updateProgress({
              stage: 'downloading-messages',
              module: 'operations',
              current,
              total,
              message,
            });
          }
        },
      });

      const syncResult = {
        success: result.failed === 0,
        cached: result.cached,
        failed: result.failed,
        cachedFormKeys: result.cachedFormKeys,
      };
      this.writeSmartTaskColdStartDuration('MASTERSYNC_FORM_TEMPLATES_READY', 'Completed MasterSync operational form template sync', startedAt, {
        workItemCount: uniqueTargets.length,
        cached: syncResult.cached,
        failed: syncResult.failed,
      });
      return syncResult;
    } catch (error) {
      if (!isSilentFetchError(error)) console.error('[MasterSync] Failed to predownload operational form templates:', error);
      this.writeSmartTaskColdStartDuration('MASTERSYNC_FORM_TEMPLATES_ERROR', 'MasterSync operational form template sync failed', startedAt, {
        workItemCount: uniqueTargets.length,
        error: error instanceof Error ? error.message : String(error),
      });
      return { success: false, cached: 0, failed: uniqueTargets.length, cachedFormKeys: [] };
    }
  }

  // ==================== PUBLISH RETRY QUEUE ====================

  /**
   * Enqueue a failed publish into the publishQueue for later retry.
   * Deduplicates by [domain+client_id+entity_type+entity_id+operation].
   */
  async enqueuePublishRetry(params: {
    domain: OfflineDomain;
    clientId: number;
    operation: string;
    entityType: string;
    entityId: string;
    payload: string;
    errorMessage?: string;
    maxAttempts?: number;
  }): Promise<void> {
    if (!offlineDb) return;
    const now = new Date().toISOString();
    const maxAttempts = params.maxAttempts ?? 5;

    const existing = await offlineDb.publishQueue
      .where('[domain+client_id+entity_type+entity_id]')
      .equals([params.domain, params.clientId, params.entityType, params.entityId])
      .and((r) => r.operation === params.operation && r.status !== 'completed' && r.status !== 'dead_letter')
      .first();

    if (existing) {
      await offlineDb.publishQueue.update(existing.id!, {
        attempts: existing.attempts + 1,
        last_attempt: now,
        updated_at: now,
        error_message: params.errorMessage || existing.error_message,
        next_retry_at: this.computeRetryBackoff(existing.attempts + 1),
        status: existing.attempts + 1 >= maxAttempts ? 'dead_letter' : 'failed',
      });
      return;
    }

    await offlineDb.publishQueue.add({
      domain: params.domain,
      client_id: params.clientId,
      operation: params.operation,
      entity_type: params.entityType,
      entity_id: params.entityId,
      status: 'pending',
      attempts: 0,
      max_attempts: maxAttempts,
      created_at: now,
      updated_at: now,
      error_message: params.errorMessage,
      next_retry_at: now,
      payload: params.payload,
    });
  }

  /**
   * Compute exponential backoff retry time.
   * Base 30s, factor 2, max 30min.
   */
  private computeRetryBackoff(attempt: number): string {
    const baseSeconds = 30;
    const maxSeconds = 30 * 60;
    const delay = Math.min(baseSeconds * Math.pow(2, attempt - 1), maxSeconds);
    return new Date(Date.now() + delay * 1000).toISOString();
  }

  /**
   * Drain the publish retry queue — attempt all due retries.
   * Called at the end of a sync cycle.
   */
  async drainPublishRetryQueue(params: {
    clientId: number;
    authToken: string;
  }): Promise<{ retried: number; succeeded: number; failed: number; deadLettered: number }> {
    if (!offlineDb) return { retried: 0, succeeded: 0, failed: 0, deadLettered: 0 };

    const now = new Date();
    const dueItems = await offlineDb.publishQueue
      .where('next_retry_at')
      .belowOrEqual(now.toISOString())
      .and((r) => (
        r.client_id === params.clientId
        && (r.status === 'pending' || r.status === 'failed')
        && !r.entity_type.startsWith('smarttask.')
      ))
      .toArray();

    let retried = 0;
    let succeeded = 0;
    let failed = 0;
    let deadLettered = 0;

    for (const item of dueItems) {
      if (item.attempts >= item.max_attempts) {
        await offlineDb.publishQueue.update(item.id!, { status: 'dead_letter', updated_at: now.toISOString() });
        deadLettered++;
        continue;
      }

      retried++;
      const attemptNum = item.attempts + 1;
      await offlineDb.publishQueue.update(item.id!, {
        status: 'processing',
        attempts: attemptNum,
        last_attempt: now.toISOString(),
        updated_at: now.toISOString(),
      });

      try {
        const payload = JSON.parse(item.payload);
        const response = await this.executePublishRetry(item, payload, params.authToken);

        await offlineDb.publishQueue.update(item.id!, {
          status: 'completed',
          updated_at: new Date().toISOString(),
          response_payload: JSON.stringify(response),
        });
        succeeded++;
      } catch (error: any) {
        const nextRetryAt = this.computeRetryBackoff(attemptNum + 1);
        const isDeadLetter = attemptNum >= item.max_attempts;
        await offlineDb.publishQueue.update(item.id!, {
          status: isDeadLetter ? 'dead_letter' : 'failed',
          updated_at: new Date().toISOString(),
          error_message: error?.message || String(error),
          next_retry_at: isDeadLetter ? undefined : nextRetryAt,
        });
        failed++;
      }
    }

    if (retried > 0) {
      debugLog('[MasterSync]', 'Publish retry queue drained: ' + JSON.stringify({ retried, succeeded, failed, deadLettered }));
    }

    return { retried, succeeded, failed, deadLettered };
  }

  /**
   * Execute a single publish retry based on the operation type.
   */
  private async executePublishRetry(
    item: PublishRetryRecord,
    payload: any,
    authToken: string,
  ): Promise<any> {
    if (!this.config) throw new Error('Not configured');

    const endpoint = this.resolvePublishRetryEndpoint(item);
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`,
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const body = await response.text().catch(() => '');
      throw new Error(`Retry ${item.operation} failed: ${response.status} ${body}`);
    }

    return response.json().catch(() => ({}));
  }

  /**
   * Resolve the API endpoint for a publish retry based on domain and operation.
   */
  private resolvePublishRetryEndpoint(item: PublishRetryRecord): string {
    const base = buildApiUrl('');
    const pathSegments: Record<string, string> = {
      'attendance:clock_in': '/api/v1/time-attendance/clock',
      'attendance:clock_out': '/api/v1/time-attendance/clock',
      'workitems:create': '/api/v1/work-items',
      'workitems:update': `/api/v1/work-items/${item.entity_id}`,
      'workitems:status_update': `/api/v1/work-items/${item.entity_id}/status`,
      'workitems:comment': `/api/v1/work-items/${item.entity_id}/comments`,
      'messages:send': '/api/v1/messages/send',
      'emails:send': '/api/v1/emails/send',
    };

    const key = `${item.domain}:${item.operation}`;
    const path = pathSegments[key];
    if (path) return `${base}${path}`;

    return `${base}/api/v1/${item.domain}/${item.operation}`;
  }

  /**
   * Get publish retry queue stats for UI display.
   */
  async getPublishRetryStats(clientId: number): Promise<{
    pending: number;
    processing: number;
    failed: number;
    dead_letter: number;
    completed: number;
  }> {
    if (!offlineDb) return { pending: 0, processing: 0, failed: 0, dead_letter: 0, completed: 0 };

    const items = await offlineDb.publishQueue
      .where('[domain+client_id]')
      .anyOf(['workitems', 'attendance', 'messages', 'emails', 'app-shell', 'offline-auth'].map(d => [d, clientId]))
      .toArray();

    return {
      pending: items.filter(r => r.status === 'pending').length,
      processing: items.filter(r => r.status === 'processing').length,
      failed: items.filter(r => r.status === 'failed').length,
      dead_letter: items.filter(r => r.status === 'dead_letter').length,
      completed: items.filter(r => r.status === 'completed').length,
    };
  }

  /**
   * Manually retry a dead-lettered item by resetting it to pending.
   */
  async retryDeadLetterItem(id: number): Promise<void> {
    if (!offlineDb) return;
    const now = new Date().toISOString();
    await offlineDb.publishQueue.update(id, {
      status: 'pending',
      attempts: 0,
      next_retry_at: now,
      updated_at: now,
      error_message: undefined,
    });
  }

  /**
   * Remove completed items older than the given number of days.
   */
  async cleanupPublishRetryQueue(daysOld: number = 7): Promise<number> {
    if (!offlineDb) return 0;
    const cutoff = new Date(Date.now() - daysOld * 24 * 60 * 60 * 1000).toISOString();
    const oldCompleted = await offlineDb.publishQueue
      .where('created_at')
      .below(cutoff)
      .and((r) => r.status === 'completed' || r.status === 'dead_letter')
      .toArray();

    for (const item of oldCompleted) {
      await offlineDb.publishQueue.delete(item.id!);
    }

    return oldCompleted.length;
  }

  // ==================== STATUS ====================

  /**
   * Check if sync is in progress
   */
  isSyncInProgress(): boolean {
    return this.isSyncing;
  }

  isOfflinePreparationInProgress(): boolean {
    return this.isPreparingOffline;
  }

  /**
   * Get current progress
   */
  getProgress(): SyncProgress {
    return this.lastProgress;
  }

  // ==================== CONFLICT MANAGEMENT ====================

  /**
   * Get all pending conflicts awaiting resolution.
   */
  getPendingConflicts(): ConflictEntry[] {
    return [...this.pendingConflicts];
  }

  /**
   * Manually resolve a conflict by ID with the given strategy and optional
   * merged data (required for 'manual' strategy).
   */
  resolveConflictById(
    conflictId: string,
    strategy: ConflictStrategy,
    mergedData?: Record<string, unknown>,
  ): boolean {
    const idx = this.pendingConflicts.findIndex(c => c.id === conflictId);
    if (idx === -1) return false;

    const conflict = this.pendingConflicts[idx];
    if (mergedData) {
      conflict.resolvedData = mergedData;
    }
    const resolved = resolveConflict(conflict, strategy);
    if (resolved) {
      this.pendingConflicts.splice(idx, 1);
      debugLog('[MasterSync]', `Conflict ${conflictId} resolved with strategy: ${strategy}`);
      return true;
    }
    return false;
  }

  /**
   * Set the default conflict resolution strategy for a module.
   */
  setConflictStrategy(module: SyncModuleName, strategy: ConflictStrategy): void {
    setModuleStrategy(module, strategy);
  }

  /**
   * Get the configured conflict resolution strategy for a module.
   */
  getConflictStrategy(module: SyncModuleName): ConflictStrategy {
    return getModuleStrategy(module);
  }

  /**
   * Record a conflict detected during a domain-specific sync operation.
   * Called by sync handlers when they detect version mismatches.
   */
  recordConflict(
    module: SyncModuleName,
    entityType: string,
    entityId: string,
    localData: Record<string, unknown>,
    serverData: Record<string, unknown>,
    conflictType?: ConflictEntry['conflictType'],
  ): void {
    const strategy = getModuleStrategy(module);
    const entry = createConflictEntry(
      module, entityType, entityId, localData, serverData, conflictType, strategy,
    );
    this.pendingConflicts.push(entry);
    debugLog('[MasterSync]', `Conflict recorded for ${module}/${entityType}/${entityId}: ${entry.conflictType}`);
  }
}

// ==================== SINGLETON EXPORT ====================

const masterSync = new MasterSync();

export default masterSync;
export { MasterSync };

// Exported for unit testing — not part of the public API.
export {
  mapUnifiedAttendanceEmployee,
  mapUnifiedAttendanceShiftTemplate,
  buildUnifiedAttendanceSnapshot,
  normalizeAttendanceOfflinePayload,
};
